import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { 
  Lightning, Crosshair, Target, TrendUp, TrendDown, Minus,
  ArrowRight, Check, X, Clock, Database, Robot, 
  ChartBar, Gear, List, House, CaretRight, SpinnerGap,
  Warning, CheckCircle, XCircle, ArrowClockwise, MagnifyingGlass,
  CalendarBlank, Skull, Star, ArrowUp, ArrowDown, NotePencil, FloppyDisk, Export,
  Lock, UserCircle, Crown, CurrencyDollar, Sparkle, Trophy, Globe
} from "@phosphor-icons/react";
import "./App.css";
import { useAuth, AuthModal, PricingPage, AccountPage, UpgradeBanner } from "./AuthAndBilling";
import { LandingPage } from "./LandingPage";
import G2PainIndex from "./G2PainIndex";
import MyBriefsView from "./MyBriefsView";
import QuickWinQuadrant from "./QuickWinQuadrant";
import { TrendingTopics } from "./TrendingTopics";
import { OpportunityPublicView } from "./OpportunityPublic";
import { BriefPublicView } from "./BriefPublic";
import AdminRoot from "./AdminDashboard";
import ApifyUsageWidget from "./components/ApifyUsageWidget";
import CookieConsentBanner from "./components/CookieConsentBanner";
import StreakBadge from "./components/StreakBadge";
import FounderProfileModal from "./components/FounderProfileModal";
import FreshnessBadge from "./components/FreshnessBadge";
import ShareOppButton from "./components/ShareOppButton";
import AIPulseView from "./components/AIPulseView";
import InstallAppBanner from "./components/InstallAppBanner";
import CountryCoverageBanner from "./components/CountryCoverageBanner";
import WaitlistSignupForm from "./components/WaitlistSignupForm";
import { computeFitScore, fitTone } from "./lib/fitScore";
import TrustCard from "./components/TrustCard";
import ProArbitrageDiscoveryBanner from "./components/ProArbitrageDiscoveryBanner";
import SideBySideOpportunityView from "./views/SideBySideOpportunityView";
import BriefBrowser from "./views/BriefBrowser";
import EvaluateBrowser from "./views/EvaluateBrowser";
import BootstrappedBrowser from "./views/BootstrappedBrowser";
import BootstrappedCategories from "./views/BootstrappedCategories";
import CorpusToggle from "./components/CorpusToggle";
import AppHeader from "./components/AppHeader";
import MobileBottomNav from "./components/MobileBottomNav";
import MobileMenuDrawer from "./components/MobileMenuDrawer";
import HelpLauncher from "./components/HelpLauncher";
import LegalPage from "./views/LegalPage";
import CountryFitBrowser from "./views/CountryFitBrowser";
import CopilotChat from "./views/CopilotChat";
import GuideDemo from "./views/GuideDemo";
import GuideHelpButton from "./components/GuideHelpButton";
import ReportsLibrary from "./views/ReportsLibrary";
import CommercePage from "./views/Commerce/CommercePage";
import JobsBoard from "./views/JobsBoard";
import PainPoints from "./views/PainPoints";
import FreshnessStamp from "./components/FreshnessStamp";

// activeTab → freshness domain (Trending stamps live inside TrendingTopics.js per-subtab)
const FRESH_DOMAIN = {
  ai_pulse: "ai_pulse",
  dashboard: "dashboard",
  opportunities: "signals",
  discover: "discover",
  evaluate: "evaluate",
  country_fit: "best_country",
  pain_points: "pain_points",
  commerce: "commerce",
  jobs: "yc_jobs",
  reports: "weekly_reports",
};
import { buildOpportunityReasoning, TONE_STYLES, SOURCE_META, getFreshness, formatNextScanCountdown, getCompetitionVerdict } from "./lib/reasoning";
import { CompetitionVerdict } from "./components/CompetitionVerdict";

// Country code -> name for the "Best country" badge on opportunity cards.
const ARB_COUNTRY_NAMES = {
  US: "United States", GB: "United Kingdom", IN: "India", CA: "Canada", AU: "Australia",
  DE: "Germany", FR: "France", ES: "Spain", IT: "Italy", NL: "Netherlands", SG: "Singapore",
  IE: "Ireland", SE: "Sweden", BR: "Brazil", JP: "Japan", AE: "United Arab Emirates",
  CH: "Switzerland", AT: "Austria", BE: "Belgium", DK: "Denmark", FI: "Finland",
  IL: "Israel", KR: "South Korea", MX: "Mexico", NO: "Norway", NZ: "New Zealand",
  PL: "Poland", PT: "Portugal", SA: "Saudi Arabia", CZ: "Czech Republic", EE: "Estonia",
};
const arbCountryName = (c) => ARB_COUNTRY_NAMES[c] || c;


// ============================================================
// FRESH TODAY SECTION — top-of-dashboard "what's new in the last 24h"
// ============================================================
const FreshTodaySection = ({ onSelectOpp, onJumpToAIPulse }) => {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [aiRun, setAiRun] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const [iRes, sRes, aRes] = await Promise.all([
          axios.get(`${API}/opportunities/fresh?hours=24&limit=8&exclude_ai=1`, { timeout: 8000 }),
          axios.get(`${API}/stats/fresh`, { timeout: 8000 }),
          axios.get(`${API}/analysis/last-run`, { timeout: 8000 }).catch(() => null),
        ]);
        if (cancelled) return;
        setItems(Array.isArray(iRes.data) ? iRes.data : []);
        setStats(sRes.data);
        if (aRes?.data) setAiRun(aRes.data);
      } catch { /* graceful degrade */ }
      if (!cancelled) setLoading(false);
    };
    load();
    const id = setInterval(load, 60000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  if (loading) return null;
  const nextIn = stats?.next_scan_at ? formatNextScanCountdown(stats.next_scan_at) : "";

  // Empty state — show the REAL reason from the last AI run, not a guess
  if (items.length === 0) {
    const aiErr = aiRun?.error || "";
    let reason;
    if (aiRun?.in_progress) {
      const b = aiRun.batches_done != null ? ` (batch ${aiRun.batches_done}/${aiRun.batches_total || "?"}, stage: ${aiRun.stage || "extracting"})` : "";
      reason = <>AI analysis is running right now{b} — new opportunities will appear when it completes.</>;
    } else if (/budget|exhausted/i.test(aiErr)) {
      reason = <span className="text-rose-600 font-medium">AI credits were exhausted — scrapers kept collecting signals, but the AI couldn't analyze them. Top up at Profile → Universal Key (auto-recharge recommended).</span>;
    } else if (aiErr) {
      reason = <>Last AI analysis failed: <span className="font-medium text-zinc-700">{aiErr.slice(0, 140)}</span></>;
    } else {
      reason = <>Either Claude was rate-limited or scrapers found no new pain signals.</>;
    }
    return (
      <div
        className="bg-white border border-zinc-200 rounded-2xl p-5 flex items-center gap-3"
        data-testid="fresh-today-empty"
      >
        <div className="w-10 h-10 rounded-full bg-zinc-100 flex items-center justify-center text-zinc-400">
          <Clock size={18} weight="duotone" />
        </div>
        <div className="flex-1">
          <div className="text-sm font-semibold text-zinc-900">No new opportunities this scan cycle</div>
          <div className="text-xs text-zinc-500" data-testid="fresh-empty-reason">
            {reason}
            {nextIn && <> Next scan in <span className="font-semibold text-zinc-700">{nextIn}</span>.</>}
          </div>
        </div>
      </div>
    );
  }

  return (
    <section
      className="bg-gradient-to-br from-emerald-50 via-white to-violet-50 border border-emerald-200 rounded-2xl p-4 sm:p-5"
      data-testid="fresh-today-section"
    >
      <div className="flex items-center justify-between mb-3 sm:mb-4 gap-2 sm:gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500 text-white text-[10px] font-bold uppercase tracking-[0.16em] animate-pulse-slow">
              🆕 Live
            </span>
            <h2 className="text-base sm:text-lg lg:text-xl font-bold text-zinc-900 tracking-tight leading-snug">
              <span className="tabular-nums">{stats?.new_today ?? items.length}</span>{" "}
              <span className="sm:hidden">new opps · last 24h</span>
              <span className="hidden sm:inline">new opportunities found in the last 24 hours</span>
            </h2>
          </div>
          <div className="text-[11px] sm:text-xs text-zinc-500">
            {stats?.new_today_ai > 0 && <><span className="font-semibold text-violet-700">{stats.new_today_ai} AI-related</span> · </>}
            <span className="hidden sm:inline">Sorted by P2P score · </span>Next scan in <span className="font-semibold text-zinc-700">{nextIn}</span>
          </div>
        </div>
        {onJumpToAIPulse && (
          <button
            onClick={onJumpToAIPulse}
            className="text-[11px] sm:text-xs font-semibold text-violet-700 hover:text-violet-900 inline-flex items-center gap-1 whitespace-nowrap"
            data-testid="fresh-today-jump-ai-pulse"
          >
            <span className="sm:hidden">AI Pulse →</span>
            <span className="hidden sm:inline">See all in AI Pulse →</span>
          </button>
        )}
      </div>

      <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1">
        {items.map(o => (
          <FreshOppPill key={o.id} opp={o} onClick={() => onSelectOpp?.(o)} />
        ))}
      </div>
    </section>
  );
};

const FreshOppPill = ({ opp, onClick }) => {
  const score = Math.round(opp.weighted_total || 0);
  const verdict = opp.verdict || "PENDING";
  const verdictBg = score >= 85 ? "bg-emerald-500" : score >= 70 ? "bg-amber-500" : score >= 50 ? "bg-zinc-400" : "bg-zinc-300";
  return (
    <button
      onClick={onClick}
      className="flex-shrink-0 w-[260px] text-left bg-white border border-zinc-100 rounded-xl p-3 hover:border-violet-300 hover:shadow-md hover:-translate-y-0.5 transition-all"
      data-testid={`fresh-pill-${opp.id}`}
    >
      <div className="flex items-center justify-between gap-2 mb-2">
        <FreshnessBadge opp={opp} />
        <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full ${verdictBg} text-white text-[9px] font-bold uppercase tracking-[0.14em]`}>{verdict}</span>
      </div>
      <p className="text-[12.5px] font-semibold text-zinc-900 leading-snug line-clamp-3 min-h-[54px]">{opp.problem}</p>
      <div className="flex items-baseline gap-1.5 mt-2 pt-2 border-t border-zinc-100">
        <span className="text-xl font-bold text-zinc-900 tabular-nums">{score}</span>
        <span className="text-[9px] uppercase tracking-[0.16em] text-zinc-500 font-semibold">P2P</span>
        {opp.ai_category && (
          <span className="ml-auto text-[9px] uppercase tracking-[0.16em] text-violet-700 font-semibold truncate max-w-[100px]">
            {opp.ai_category}
          </span>
        )}
      </div>
    </button>
  );
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ============================================================
// UTILITIES
// ============================================================
const formatTimestamp = (raw) => {
  if (!raw) return "";
  // Handles both ISO ("2026-04-25T06:14:18+00:00") and SQL ("2026-04-23 01:26:51")
  const iso = typeof raw === "string" && raw.includes(" ") && !raw.includes("T")
    ? raw.replace(" ", "T") + "Z"
    : raw;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return String(raw);
  const now = new Date();
  const diffMs = now - d;
  const diffMin = Math.floor(diffMs / 60000);
  const diffH = Math.floor(diffMs / 3600000);
  const diffD = Math.floor(diffMs / 86400000);
  if (diffMin < 1) return "just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffH < 24) return `${diffH}h ago`;
  if (diffD < 7) return `${diffD}d ago`;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })
    + " " + d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
};

// ============================================================
// UTILITY COMPONENTS
// ============================================================
// LLMHealthIndicator moved to ./components/LLMHealthIndicator

const Badge = ({ children, variant = "default", className = "" }) => {
  const variants = {
    default: "bg-zinc-50 text-zinc-700 border border-zinc-200",
    success: "bg-emerald-500/10 text-emerald-400 border border-emerald-500/30",
    warning: "bg-violet-600/10 text-violet-600 border border-violet-200",
    danger:  "bg-red-500/10 text-red-400 border border-red-500/30",
    strong:  "bg-emerald-500/10 text-emerald-400 border border-emerald-500/30",
    moderate:"bg-violet-600/10 text-violet-600 border border-violet-200",
    weak:    "bg-zinc-50 text-zinc-500 border border-zinc-200",
  };
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 text-[10px] font-mono-data font-semibold uppercase tracking-[0.12em] rounded-full ${variants[variant]} ${className}`}>
      {children}
    </span>
  );
};

const Button = ({ children, variant = "primary", onClick, disabled, className = "", ...props }) => {
  const variants = {
    primary:   "bg-violet-600 text-white hover:bg-violet-500 shadow-[0_0_24px_-4px_rgba(255,107,26,0.5)]",
    secondary: "bg-zinc-50 text-zinc-900 hover:bg-zinc-200 border border-zinc-200",
    danger:    "bg-red-500/10 text-red-400 border border-red-500/40 hover:bg-red-500/20",
    ghost:     "bg-transparent text-zinc-700 hover:text-zinc-900 hover:bg-zinc-50",
  };
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`px-5 py-2.5 font-medium text-sm rounded-full transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed ${variants[variant]} ${className}`}
      {...props}
    >
      {children}
    </button>
  );
};

const Card = ({ children, className = "", onClick }) => (
  <div 
    onClick={onClick}
    className={`bg-white border border-zinc-200 rounded-2xl p-5 sm:p-6 card-hover ${onClick ? 'cursor-pointer' : ''} ${className}`}
  >
    {children}
  </div>
);

const PillCaption = ({ children }) => (
  <span className="p2p-pill-caption font-medium" data-testid="pill-caption">
    {children}
  </span>
);

const StatCard = ({ label, value, icon: Icon, trend, accent }) => (
  <Card className="flex flex-col relative overflow-hidden">
    <div className="flex items-center justify-between mb-3">
      <span className="p2p-overline">{label}</span>
      {Icon && <Icon size={16} className="text-zinc-500" weight="duotone" />}
    </div>
    <div className={`text-3xl sm:text-4xl font-bold tracking-tight ${accent || 'text-zinc-900'}`}>{value}</div>
    {trend !== undefined && trend !== null && (
      <div className="flex items-center gap-1 mt-2 text-xs font-mono-data">
        {trend > 0 ? <TrendUp className="text-emerald-400" size={12} /> : trend < 0 ? <TrendDown className="text-red-400" size={12} /> : <Minus size={12} />}
        <span className="text-zinc-500">{Math.abs(trend)}% w/w</span>
      </div>
    )}
  </Card>
);

const ScoreBar = ({ label, value, weight }) => (
  <div className="flex flex-col gap-1">
    <div className="flex justify-between text-xs">
      <span className="p2p-overline">{label}</span>
      <span className="font-mono-data font-bold text-zinc-900">{value}</span>
    </div>
    <div className="h-1 bg-zinc-200 rounded-full overflow-hidden">
      <div 
        className="h-full bg-violet-600 rounded-full transition-all duration-500"
        style={{ width: `${value}%` }}
      />
    </div>
    <span className="text-[10px] text-zinc-500 font-mono-data">{weight}% weight</span>
  </div>
);

const TrendIndicator = ({ direction, showLabel = false }) => {
  const config = {
    GROWING:   { icon: TrendUp,    color: "text-emerald-400", bg: "bg-emerald-500/10 border border-emerald-500/30", label: "Growing" },
    DECLINING: { icon: TrendDown,  color: "text-red-400",     bg: "bg-red-500/10 border border-red-500/30",         label: "Declining" },
    STABLE:    { icon: Minus,      color: "text-zinc-500",   bg: "bg-zinc-50 border border-zinc-200",           label: "Stable" },
    NEW:       { icon: ArrowRight, color: "text-violet-600",  bg: "bg-violet-600/10 border border-violet-200",   label: "New" },
  };
  const c = config[direction] || config.NEW;
  const IconComp = c.icon;
  
  if (showLabel) {
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-mono-data font-semibold uppercase tracking-[0.12em] rounded-full ${c.color} ${c.bg}`} data-testid={`trend-${direction}`}>
        <IconComp size={12} weight="bold" />
        {c.label}
      </span>
    );
  }
  return <IconComp size={14} className={c.color} weight="bold" data-testid={`trend-icon-${direction}`} />;
};

// ============================================================
// MAIN COMPONENTS
// ============================================================
const LockedOpportunityCard = ({ onUpgrade }) => (
  <div
    onClick={onUpgrade}
    className="group relative bg-white border border-zinc-200 rounded-2xl p-5 sm:p-6 cursor-pointer overflow-hidden card-hover hover:border-violet-300"
    data-testid="locked-opportunity-card"
  >
    <div className="absolute inset-0 pointer-events-none flex flex-col justify-center px-6 select-none" aria-hidden>
      <div className="h-2 w-3/4 bg-zinc-200 rounded-full mb-2 blur-[2px]" />
      <div className="h-2 w-1/2 bg-zinc-200 rounded-full mb-4 blur-[2px]" />
      <div className="h-3 w-2/3 bg-[#3F3A33] rounded-full blur-[3px]" />
    </div>
    <div className="relative flex items-center justify-between gap-3">
      <div className="flex items-center gap-3 min-w-0">
        <div className="w-11 h-11 bg-violet-600/10 border border-violet-300 rounded-xl flex items-center justify-center flex-shrink-0 p2p-glow-violet-soft">
          <Lock size={18} weight="fill" className="text-violet-600 p2p-lock-icon" />
        </div>
        <div className="min-w-0">
          <div className="font-mono-data text-[10px] uppercase tracking-[0.18em] text-violet-600 font-semibold">Pro signal · locked</div>
          <div className="text-base font-semibold text-zinc-900 mt-0.5">Premium opportunity hidden</div>
          <div className="text-xs text-zinc-500 mt-0.5 truncate">Founder $19/mo unlocks every scored signal.</div>
        </div>
      </div>
      <button
        onClick={(e) => { e.stopPropagation(); onUpgrade(); }}
        className="bg-violet-600 hover:bg-violet-500 text-white text-xs font-semibold rounded-full px-4 py-2 whitespace-nowrap shadow-[0_0_18px_-4px_rgba(255,107,26,0.5)]"
        data-testid="locked-card-upgrade-btn"
      >
        Unlock →
      </button>
    </div>
  </div>
);

// ============================================================
// Shared "Why this matters" reasoning + share button (used by every card)
// ============================================================
const OpportunityReasoning = ({ opportunity, compact = false }) => {
  const reasoning = buildOpportunityReasoning(opportunity);
  const comp = getCompetitionVerdict(opportunity);
  if (reasoning.lines.length === 0 && !comp) return null;
  const tone = TONE_STYLES[reasoning.tone];
  return (
    <div className="mt-3 pt-3 border-t border-zinc-200" data-testid={`opp-reasoning-${opportunity.id}`}>
      <div className="text-[10px] uppercase tracking-[0.18em] text-violet-700 font-bold mb-1.5">Why this matters</div>
      {reasoning.lines.length > 0 && (
        <ul className="space-y-1">
          {reasoning.lines.slice(0, compact ? 2 : 4).map((line, i) => (
            <li key={i} className="text-[11.5px] text-zinc-700 leading-snug flex gap-1.5">
              <span className="flex-shrink-0">{line.icon}</span>
              <span>{line.text}</span>
            </li>
          ))}
        </ul>
      )}
      {comp ? (
        <CompetitionVerdict opportunity={opportunity} compact={compact} />
      ) : (
        <div className={`mt-2 px-2.5 py-1.5 rounded-md border ${tone.bg} ${tone.border} ${tone.text}`}>
          <div className="text-[9px] uppercase tracking-[0.22em] font-bold opacity-70 mb-0.5">Our take · {tone.label}</div>
          <div className="text-[11px] font-semibold leading-snug">{reasoning.action}</div>
        </div>
      )}
    </div>
  );
};

const OpportunityCard = ({ opportunity, onSelect, onAction, onNotesUpdate, isStarred, onToggleStar, requireAuth, onUpgrade }) => {
  const [notes, setNotes] = useState(opportunity.notes || "");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const debounceRef = useRef(null);

  if (opportunity.locked) {
    return <LockedOpportunityCard onUpgrade={onUpgrade} />;
  }
  const score = opportunity.weighted_total || 0;
  const verdict = opportunity.verdict || "N/A";
  const signal = opportunity.signal_strength || "WEAK";
  
  const getScoreColor = (s) => {
    if (s >= 85) return "score-high";
    if (s >= 70) return "score-medium";
    if (s >= 50) return "score-low";
    return "score-dead";
  };

  const getVerdictBadge = (v) => {
    if (v === "HOME RUN") return "success";
    if (v === "PROMISING") return "warning";
    if (v === "LUKEWARM") return "default";
    return "danger";
  };

  const getSignalBadge = (s) => {
    if (s === "STRONG") return "strong";
    if (s === "MODERATE") return "moderate";
    return "weak";
  };

  const saveNotes = (val) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setSaving(true);
      try {
        await axios.put(`${API}/opportunities/${opportunity.id}/notes`, { notes: val });
        setSaved(true);
        if (onNotesUpdate) onNotesUpdate(opportunity.id, val);
        setTimeout(() => setSaved(false), 1500);
      } catch (err) {
        console.warn("Notes save failed:", err?.response?.status || err?.message);
      }
      setSaving(false);
    }, 600);
  };

  const handleNotesChange = (e) => {
    const val = e.target.value;
    setNotes(val);
    saveNotes(val);
  };

  return (
    <Card 
      onClick={() => onSelect(opportunity)}
      className={`relative ${
        score >= 85 ? 'border-emerald-500/40 p2p-glow-emerald'
        : score >= 70 ? 'border-violet-300'
        : score >= 50 ? 'border-zinc-300'
        : 'border-red-500/30 opacity-80'
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 sm:gap-2 mb-2 flex-wrap">
            <FreshnessBadge opp={opportunity} />
            {opportunity.__best_country && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  window.dispatchEvent(new CustomEvent("pp:open-country-fit"));
                }}
                data-testid={`opp-best-country-${opportunity.id}`}
                className="text-[10px] font-semibold border border-violet-300 bg-violet-50 text-violet-700 pl-1.5 pr-2 py-0.5 rounded-full flex items-center gap-1 hover:bg-violet-600 hover:text-white hover:border-violet-600 transition-colors"
                title={`Best country to launch: ${arbCountryName(opportunity.__best_country)} — open the Best Country page`}
              >
                <Globe size={11} weight="duotone" /> Best: {arbCountryName(opportunity.__best_country)}
              </button>
            )}
            <Badge variant={getVerdictBadge(verdict)}>{verdict}</Badge>
            <Badge variant={getSignalBadge(signal)}>{signal}</Badge>
            <TrendIndicator direction={opportunity.trend_direction} showLabel />
            {opportunity.gtrends_signal && (() => {
              const g = opportunity.gtrends_signal;
              const tone =
                g.status === "rising"
                  ? "text-emerald-700 bg-emerald-50 border-emerald-300"
                  : g.status === "cooling"
                  ? "text-rose-700 bg-rose-50 border-rose-300"
                  : "text-zinc-600 bg-zinc-50 border-zinc-300";
              const arrow = g.status === "rising" ? "🔥" : g.status === "cooling" ? "❄️" : "➖";
              const pct = g.growth_pct > 0 ? `+${Math.round(g.growth_pct)}%` : `${Math.round(g.growth_pct)}%`;
              return (
                <span
                  className={`text-[10px] font-mono-data font-semibold border px-1.5 py-0.5 uppercase tracking-[0.15em] rounded-sm flex items-center gap-1 ${tone}`}
                  title={`Google Trends: "${g.keyword}" ${pct} over 3 months (interest ${g.interest_score}/100)`}
                  data-testid={`opp-gtrends-${opportunity.id}`}
                >
                  <span>{arrow}</span> {pct}
                </span>
              );
            })()}
            {opportunity.niche_label && (
              <span
                className={`text-[10px] font-mono-data font-semibold border px-1.5 py-0.5 uppercase tracking-[0.15em] rounded-sm flex items-center gap-1 ${opportunity.is_favorite_niche ? 'text-amber-800 bg-amber-50 border-amber-300' : 'text-teal-700 bg-teal-50 border-teal-200'}`}
                data-testid={`opp-niche-${opportunity.id}`}
              >
                {opportunity.is_favorite_niche && <span className="text-amber-500">★</span>}
                {opportunity.niche_label}
              </span>
            )}
            {/* Fit Score chip — only present when fit-ranking is active on the parent list */}
            {typeof opportunity.__fit === "number" && (() => {
              const tone = fitTone(opportunity.__fit);
              if (!tone) return null;
              return (
                <span
                  className={`text-[10px] font-mono-data font-semibold ring-1 px-1.5 py-0.5 uppercase tracking-[0.15em] rounded-sm flex items-center gap-1 ${tone.cls}`}
                  title={`Match score: ${tone.label} for your founder profile`}
                  data-testid={`opp-fit-${opportunity.id}`}
                  data-fit-score={opportunity.__fit}
                >
                  🎯 Fit {opportunity.__fit}
                </span>
              );
            })()}
            {opportunity.is_rising && (
              <span
                className="text-[10px] font-mono-data font-semibold text-rose-700 bg-rose-50 border border-rose-300 px-1.5 py-0.5 uppercase tracking-[0.15em] rounded-sm flex items-center gap-1 animate-pulse"
                title="Crossed into the Quick-Win zone in the last 7 days — momentum is accelerating"
                data-testid={`opp-rising-${opportunity.id}`}
              >
                <span>🚀</span> Rising
              </span>
            )}
            {opportunity.validation_tier === "triple" && (
              <span
                className="text-[10px] font-mono-data font-semibold text-emerald-700 bg-emerald-50 border border-emerald-300 px-1.5 py-0.5 uppercase tracking-[0.15em] rounded-sm flex items-center gap-1"
                title={`Triple-sourced across ${opportunity.validation_sources?.join(", ")}`}
                data-testid={`opp-validation-${opportunity.id}`}
              >
                <span>✓✓✓</span> 3-SOURCE
              </span>
            )}
            {opportunity.validation_tier === "double" && (
              <span
                className="text-[10px] font-mono-data font-semibold text-cyan-700 bg-cyan-50 border border-cyan-200 px-1.5 py-0.5 uppercase tracking-[0.15em] rounded-sm flex items-center gap-1"
                title={`Double-sourced across ${opportunity.validation_sources?.join(", ")}`}
                data-testid={`opp-validation-${opportunity.id}`}
              >
                <span>✓✓</span> 2-SOURCE
              </span>
            )}
            {opportunity.consultant_replacement === 1 && (
              <Badge variant="success">CONSULTANT</Badge>
            )}
            {opportunity.status === "MANUAL_IDEA" && (
              <span className="text-[10px] font-mono-data font-semibold text-violet-600 bg-violet-600/10 border border-violet-200 px-1.5 py-0.5 uppercase tracking-[0.15em]">My Idea</span>
            )}
          </div>
          <h3 className="font-display text-base sm:text-lg font-bold text-zinc-900 leading-tight mb-2 line-clamp-2">
            {opportunity.problem || "Unknown Problem"}
          </h3>
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-zinc-500 font-mono-data">
            <span className="flex items-center gap-1">
              <Database size={12} weight="duotone" />
              {opportunity.reddit_frequency || 0}r · {opportunity.freelancer_frequency || 0}f
            </span>
            <span className="flex items-center gap-1 truncate max-w-[180px]">
              <Target size={12} weight="duotone" />
              {opportunity.user_type || "N/A"}
            </span>
            {opportunity.suggested_price > 0 && (
              <span className="font-bold text-violet-600">
                ${opportunity.suggested_price}/mo
              </span>
            )}
          </div>
        </div>
        <div className="flex flex-col items-end gap-2 flex-shrink-0">
          <div className={`w-14 h-14 sm:w-16 sm:h-16 ${getScoreColor(score)} flex items-center justify-center`}>
            <span className="font-mono-data text-xl sm:text-2xl font-bold p2p-score-anim">{Math.round(score)}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <ShareOppButton oppId={opportunity.id} testId={`opp-share-${opportunity.id}`} />
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (requireAuth && requireAuth()) return;
                onToggleStar && onToggleStar(opportunity.id);
              }}
              title={isStarred ? "Remove from watchlist" : "Add to watchlist"}
              className={`p-1 transition-colors ${isStarred ? 'text-violet-600 drop-shadow-[0_0_4px_rgba(255,192,0,0.5)]' : 'text-zinc-700 hover:text-violet-600'}`}
              data-testid={`star-btn-${opportunity.id}`}
            >
              <Star size={18} weight={isStarred ? "fill" : "regular"} />
            </button>
          </div>
        </div>
      </div>
      
      {opportunity.gap && (
        <p className="mt-3 text-xs sm:text-sm text-zinc-500 line-clamp-2">
          <span className="font-mono-data text-[10px] uppercase tracking-[0.15em] text-zinc-500">Gap →</span>{" "}{opportunity.gap}
        </p>
      )}

      {/* WHY THIS MATTERS + OUR TAKE — applies to every opportunity, AI or not */}
      <OpportunityReasoning opportunity={opportunity} />

      {/* Notes */}
      <div className="mt-3 pt-3 border-t border-zinc-200" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-1.5 mb-1">
          <NotePencil size={11} className="text-zinc-500" />
          <span className="p2p-overline">Notes</span>
          {saving && <span className="text-[10px] text-zinc-500 font-mono-data">saving…</span>}
          {saved && <span className="text-[10px] text-emerald-400 font-mono-data font-semibold">saved</span>}
        </div>
        <textarea
          value={notes}
          onChange={handleNotesChange}
          placeholder="Add a note…"
          rows={1}
          className="w-full px-2 py-1.5 text-xs text-zinc-800 bg-white border border-zinc-200 resize-none focus:outline-none focus:border-violet-600 placeholder:text-zinc-500 font-body"
          data-testid={`notes-input-${opportunity.id}`}
        />
      </div>
      
      {/* Timestamps */}
      <div className="mt-3 pt-3 border-t border-zinc-200 flex flex-wrap gap-x-4 gap-y-1 text-[10px] text-zinc-500 font-mono-data">
        {opportunity.created_date && (
          <span data-testid={`opp-created-${opportunity.id}`}>
            <span className="uppercase tracking-[0.15em] text-zinc-700 mr-1">Found</span>
            {formatTimestamp(opportunity.created_date)}
          </span>
        )}
        {opportunity.scored_date && (
          <span data-testid={`opp-scored-${opportunity.id}`}>
            <span className="uppercase tracking-[0.15em] text-zinc-700 mr-1">Scored</span>
            {formatTimestamp(opportunity.scored_date)}
          </span>
        )}
        {opportunity.updated_date && opportunity.updated_date !== opportunity.created_date && (
          <span data-testid={`opp-updated-${opportunity.id}`}>
            <span className="uppercase tracking-[0.15em] text-zinc-700 mr-1">Updated</span>
            {formatTimestamp(opportunity.updated_date)}
          </span>
        )}
      </div>

      <div className="flex gap-2 mt-3 pt-3 border-t border-zinc-200">
        <Button 
          variant="secondary" 
          className="flex-1 text-xs sm:text-sm"
          onClick={(e) => { e.stopPropagation(); onAction(opportunity.id, "validate"); }}
          data-testid={`validate-btn-${opportunity.id}`}
        >
          <Check size={14} className="mr-1" /> Validate
        </Button>
        <Button 
          variant="danger" 
          className="flex-1 text-xs sm:text-sm"
          onClick={(e) => { e.stopPropagation(); onAction(opportunity.id, "kill"); }}
          data-testid={`kill-btn-${opportunity.id}`}
        >
          <X size={14} className="mr-1" /> Kill
        </Button>
      </div>
    </Card>
  );
};

const OpportunityDetailArbitrageToggle = ({ opportunity, initialMode = "free" }) => {
  const [mode, setMode] = useState(initialMode);
  const onUpgrade = () => {
    // Defer to App-level upgrade flow if available; else redirect to /pricing
    window.location.href = "/pricing";
  };
  return (
    <div data-testid="arbitrage-toggle-wrap" className="space-y-3">
      <div className="flex items-center gap-2" role="tablist" aria-label="Opportunity view mode">
        <button
          type="button"
          data-testid="view-mode-free-btn"
          onClick={() => setMode("free")}
          className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wider border transition-colors ${
            mode === "free"
              ? "bg-zinc-900 text-white border-zinc-900"
              : "bg-white text-zinc-700 border-zinc-200 hover:border-zinc-400"
          }`}
        >
          Free Prime Pillar
        </button>
        <button
          type="button"
          data-testid="view-mode-arbitrage-btn"
          onClick={() => setMode("arbitrage")}
          className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wider border transition-colors inline-flex items-center gap-1.5 ${
            mode === "arbitrage"
              ? "bg-[#7C3AED] text-white border-[#7C3AED]"
              : "bg-white text-[#7C3AED] border-[#7C3AED]/30 hover:border-[#7C3AED]"
          }`}
        >
          🌍 Where to Launch + Why
          <span className="text-[9px] font-mono px-1 bg-white/20">$199/mo</span>
        </button>
      </div>
      {mode === "free" ? (
        <TrustCard opportunityId={opportunity.id} />
      ) : (
        <SideBySideOpportunityView opportunity={opportunity} onUpgradeClick={onUpgrade} />
      )}
    </div>
  );
};

const OpportunityDetail = ({ opportunity, onClose, onAction }) => {
  if (!opportunity) return null;

  const score = opportunity.weighted_total || 0;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 sm:p-4" onClick={onClose}>
      <div 
        className="bg-white w-full sm:max-w-6xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-white border-b border-zinc-200 p-6 flex items-center justify-between">
          <h2 className="text-2xl font-black tracking-tight">Opportunity Detail</h2>
          <button onClick={onClose} className="p-2 hover:bg-white" data-testid="close-detail-btn">
            <X size={24} />
          </button>
        </div>
        
        <div className="p-6 space-y-6">
          <div className="flex items-start gap-4">
            <div className={`w-20 h-20 flex-shrink-0 ${score >= 85 ? 'bg-emerald-500' : score >= 70 ? 'bg-[#7C3AED]' : score >= 50 ? 'bg-zinc-400' : 'bg-red-500'} flex items-center justify-center`}>
              <span className="text-3xl font-black text-white">{Math.round(score)}</span>
            </div>
            <div>
              <div className="flex gap-2 mb-2">
                <Badge variant={opportunity.verdict === "HOME RUN" ? "success" : opportunity.verdict === "PROMISING" ? "warning" : "default"}>
                  {opportunity.verdict}
                </Badge>
                <Badge variant={opportunity.signal_strength === "STRONG" ? "strong" : opportunity.signal_strength === "MODERATE" ? "moderate" : "weak"}>
                  {opportunity.signal_strength}
                </Badge>
                <TrendIndicator direction={opportunity.trend_direction} showLabel />
              </div>
              <h3 className="text-xl font-bold text-zinc-900">{opportunity.problem}</h3>
              <p className="text-zinc-500">{opportunity.user_type}</p>
              <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-zinc-500">
                {opportunity.created_date && (
                  <span><span className="font-semibold">Found:</span> {formatTimestamp(opportunity.created_date)}</span>
                )}
                {opportunity.scored_date && (
                  <span><span className="font-semibold">Scored:</span> {formatTimestamp(opportunity.scored_date)}</span>
                )}
                {opportunity.updated_date && opportunity.updated_date !== opportunity.created_date && (
                  <span><span className="font-semibold">Updated:</span> {formatTimestamp(opportunity.updated_date)}</span>
                )}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 p-4 bg-zinc-50 border border-zinc-200">
            <div>
              <span className="text-xs font-bold uppercase tracking-wider text-zinc-500">Reddit Mentions</span>
              <p className="text-2xl font-bold">{opportunity.reddit_frequency || 0}</p>
            </div>
            <div>
              <span className="text-xs font-bold uppercase tracking-wider text-zinc-500">Freelancer Jobs</span>
              <p className="text-2xl font-bold">{opportunity.freelancer_frequency || 0}</p>
            </div>
            <div>
              <span className="text-xs font-bold uppercase tracking-wider text-zinc-500">Budget Range</span>
              <p className="text-lg font-semibold">{opportunity.freelancer_budget_range || "N/A"}</p>
            </div>
            <div>
              <span className="text-xs font-bold uppercase tracking-wider text-zinc-500">Suggested Price</span>
              <p className="text-lg font-semibold">${opportunity.suggested_price || 0}/mo</p>
            </div>
          </div>

          <div>
            <OpportunityDetailArbitrageToggle
              opportunity={opportunity}
              initialMode={opportunity?.__open_in_arbitrage ? "arbitrage" : "free"}
            />
          </div>

          <div>
            <h4 className="text-sm font-bold uppercase tracking-wider text-zinc-500 mb-3">Score Breakdown</h4>
            <div className="grid grid-cols-1 gap-3">
              <ScoreBar label="Demand" value={opportunity.demand_score || 0} weight={30} />
              <ScoreBar label="Competition Gap" value={opportunity.competition_score || 0} weight={25} />
              <ScoreBar label="Automation Fit" value={opportunity.automation_score || 0} weight={20} />
              <ScoreBar label="Revenue Potential" value={opportunity.revenue_score || 0} weight={15} />
              <ScoreBar label="Speed to Launch" value={opportunity.speed_score || 0} weight={10} />
            </div>
            
            {(opportunity.score_consultant_boost > 0 || opportunity.score_trend_boost !== 0) && (
              <div className="mt-4 p-3 bg-emerald-500/10 border border-emerald-200">
                <span className="text-xs font-bold uppercase tracking-wider text-emerald-700">Boosts Applied</span>
                <div className="flex gap-4 mt-1">
                  {opportunity.score_consultant_boost > 0 && (
                    <span className="text-sm font-semibold text-emerald-700">
                      +{opportunity.score_consultant_boost} Consultant Replacement
                    </span>
                  )}
                  {opportunity.score_trend_boost !== 0 && (
                    <span className={`text-sm font-semibold ${opportunity.score_trend_boost > 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                      {opportunity.score_trend_boost > 0 ? '+' : ''}{opportunity.score_trend_boost} Trend
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="space-y-3">
            <div>
              <span className="text-xs font-bold uppercase tracking-wider text-zinc-500">Gap / Opportunity</span>
              <p className="text-zinc-800 mt-1">{opportunity.gap || "Not specified"}</p>
            </div>
            <div>
              <span className="text-xs font-bold uppercase tracking-wider text-zinc-500">Existing Solutions</span>
              <p className="text-zinc-800 mt-1">{opportunity.existing_solutions || "None found"}</p>
            </div>
            <div>
              <span className="text-xs font-bold uppercase tracking-wider text-zinc-500">Automation Potential</span>
              <p className="text-zinc-800 mt-1">{opportunity.automation_potential || "Not assessed"}</p>
            </div>
            <div>
              <span className="text-xs font-bold uppercase tracking-wider text-zinc-500">Revenue Model</span>
              <p className="text-zinc-800 mt-1">{opportunity.revenue_model || "N/A"}</p>
            </div>
            {opportunity.reasoning && (
              <div>
                <span className="text-xs font-bold uppercase tracking-wider text-zinc-500">AI Analysis</span>
                <p className="text-zinc-800 mt-1 p-3 bg-zinc-50 border border-zinc-200">{opportunity.reasoning}</p>
              </div>
            )}
          </div>

          <DeepDiveSection opportunityId={opportunity.id} />

          <div className="flex gap-3 pt-4 border-t border-zinc-200">
            <Button 
              variant="secondary" 
              className="flex-1"
              onClick={() => onAction(opportunity.id, "validate")}
              data-testid="detail-validate-btn"
            >
              <CheckCircle size={18} className="mr-2" /> Validate & Move to Phase 3
            </Button>
            <Button 
              variant="danger"
              onClick={() => onAction(opportunity.id, "kill")}
              data-testid="detail-kill-btn"
            >
              <XCircle size={18} className="mr-2" /> Kill
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================================
// AI DEEP-DIVE REPORT (Founder-grade analysis per opportunity)
// ============================================================
const DeepDiveSection = ({ opportunityId }) => {
  const { user } = useAuth();
  const [report, setReport] = useState(null);
  const [generatedAt, setGeneratedAt] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fetchedCache, setFetchedCache] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);

  const downloadPdf = async () => {
    setPdfBusy(true);
    try {
      const r = await axios.get(`${API}/me/deep-dive/${opportunityId}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `paintoprofit-opp-${opportunityId}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert(e?.response?.data?.detail || "PDF export failed (Pro feature)");
    } finally {
      setPdfBusy(false);
    }
  };

  // On mount, load cached report if any
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/opportunities/${opportunityId}/deep-dive`);
        if (!cancelled && res.data?.deep_dive) {
          setReport(res.data.deep_dive);
          setGeneratedAt(res.data.generated_at);
        }
      } catch (err) {
        if (!cancelled) console.warn("Deep-dive cache fetch failed:", err?.response?.status || err?.message);
      } finally {
        if (!cancelled) setFetchedCache(true);
      }
    })();
    return () => { cancelled = true; };
  }, [opportunityId]);

  const generate = async (force = false) => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.post(`${API}/opportunities/${opportunityId}/deep-dive${force ? "?force=true" : ""}`, {}, { timeout: 120000 });
      setReport(res.data.deep_dive);
      setGeneratedAt(res.data.generated_at);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  };

  if (!fetchedCache) return null;

  return (
    <div className="border-t border-zinc-200 pt-4" data-testid="deep-dive-section">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div>
          <h4 className="text-sm font-bold uppercase tracking-wider text-zinc-900">🚀 AI Deep-Dive Report</h4>
          <p className="text-xs text-zinc-500 mt-0.5">Founder-grade: market, competitors, MVP, landing page, validation tweets</p>
        </div>
        {report ? (
          <div className="flex items-center gap-3">
            {user?.tier === "pro" && (
              <button
                onClick={downloadPdf}
                disabled={pdfBusy}
                className="text-xs font-semibold text-violet-700 hover:underline disabled:opacity-50 flex items-center gap-1"
                data-testid="export-deep-dive-pdf-btn"
              >
                {pdfBusy ? "Generating PDF…" : "📄 Export PDF"}
              </button>
            )}
            <button
              onClick={() => generate(true)}
              disabled={loading}
              className="text-xs font-semibold text-[#002FA7] hover:underline disabled:opacity-50"
              data-testid="regenerate-deep-dive-btn"
            >
              {loading ? "Regenerating…" : "Regenerate"}
            </button>
          </div>
        ) : (
          <Button
            onClick={() => generate(false)}
            disabled={loading}
            className="bg-[#002FA7] text-white hover:bg-[#001d6e] disabled:opacity-50"
            data-testid="generate-deep-dive-btn"
          >
            {loading ? "Generating (40-60s)…" : "Generate Report"}
          </Button>
        )}
      </div>

      {error && (
        <div className="p-3 bg-red-500/10 border border-red-500/30 text-sm text-red-700" data-testid="deep-dive-error">
          {error}
        </div>
      )}

      {report && (
        <div className="space-y-4 mt-2" data-testid="deep-dive-content">
          {report.verdict && (
            <div className="p-3 bg-violet-600/10 border border-violet-200">
              <span className="text-xs font-bold uppercase tracking-wider text-violet-600">Verdict</span>
              <p className="text-sm font-semibold text-violet-600 mt-1">{report.verdict}</p>
            </div>
          )}

          {report.problem_deep_dive && (
            <div>
              <span className="text-xs font-bold uppercase tracking-wider text-zinc-500">Problem Analysis</span>
              <p className="text-sm text-zinc-800 mt-1">{report.problem_deep_dive}</p>
            </div>
          )}

          {Array.isArray(report.user_quotes) && report.user_quotes.length > 0 && (
            <div>
              <span className="text-xs font-bold uppercase tracking-wider text-zinc-500">Voice of the Customer</span>
              <div className="mt-1 space-y-1">
                {report.user_quotes.map((q, i) => (
                  <p key={`q-${i}-${(q || "").slice(0,20)}`} className="text-sm italic text-zinc-700 pl-3 border-l-2 border-zinc-300">"{q}"</p>
                ))}
              </div>
            </div>
          )}

          {Array.isArray(report.competitor_analysis) && report.competitor_analysis.length > 0 && (
            <div>
              <span className="text-xs font-bold uppercase tracking-wider text-zinc-500">Competitor Landscape</span>
              <div className="mt-1 space-y-2">
                {report.competitor_analysis.map((c, i) => (
                  <div key={`comp-${c.name || i}`} className="p-2 bg-zinc-50 border border-zinc-200 text-sm">
                    <div className="font-semibold text-zinc-900">
                      {c.url ? <a href={c.url} target="_blank" rel="noreferrer" className="underline hover:text-[#002FA7]">{c.name}</a> : c.name}
                      {c.pricing && <span className="ml-2 text-xs text-zinc-500">{c.pricing}</span>}
                    </div>
                    {c.what_they_do && <div className="text-zinc-700 mt-0.5">{c.what_they_do}</div>}
                    {c.gap && <div className="text-emerald-700 mt-0.5"><span className="font-semibold">Gap:</span> {c.gap}</div>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {report.market_size && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-sm">
              <div className="p-2 bg-zinc-50 border border-zinc-200">
                <span className="text-xs font-bold uppercase tracking-wider text-zinc-500">TAM</span>
                <p className="text-zinc-900 mt-1">{report.market_size.tam_usd}</p>
              </div>
              <div className="p-2 bg-zinc-50 border border-zinc-200">
                <span className="text-xs font-bold uppercase tracking-wider text-zinc-500">SAM</span>
                <p className="text-zinc-900 mt-1">{report.market_size.sam_usd}</p>
              </div>
              <div className="p-2 bg-zinc-50 border border-zinc-200">
                <span className="text-xs font-bold uppercase tracking-wider text-zinc-500">Confidence</span>
                <p className="text-zinc-900 mt-1 font-semibold">{report.market_size.confidence}</p>
              </div>
            </div>
          )}

          {report.mvp_scope_5_days && (
            <div>
              <span className="text-xs font-bold uppercase tracking-wider text-zinc-500">5-Day MVP Plan</span>
              <ol className="mt-1 space-y-1 text-sm">
                {["day_1","day_2","day_3","day_4","day_5"].map((k, i) => report.mvp_scope_5_days[k] && (
                  <li key={k} className="flex gap-2">
                    <span className="font-bold text-[#002FA7] flex-shrink-0">Day {i+1}:</span>
                    <span className="text-zinc-800">{report.mvp_scope_5_days[k]}</span>
                  </li>
                ))}
              </ol>
              {report.tech_stack && (
                <p className="text-xs text-zinc-500 mt-2"><span className="font-semibold">Stack:</span> {report.tech_stack}</p>
              )}
            </div>
          )}

          {report.landing_page && (
            <div className="p-3 bg-emerald-500/10 border border-emerald-500/30">
              <span className="text-xs font-bold uppercase tracking-wider text-emerald-700">Landing Page Copy (ready to use)</span>
              <h5 className="text-base font-bold text-zinc-900 mt-1">{report.landing_page.headline}</h5>
              {report.landing_page.subheadline && (
                <p className="text-sm text-zinc-700 mt-1">{report.landing_page.subheadline}</p>
              )}
              {Array.isArray(report.landing_page.bullets) && (
                <ul className="text-sm text-zinc-800 mt-2 list-disc pl-5 space-y-0.5">
                  {report.landing_page.bullets.map((b, i) => <li key={`bullet-${i}-${(b || "").slice(0,20)}`}>{b}</li>)}
                </ul>
              )}
              {report.landing_page.cta && (
                <button className="mt-3 px-4 py-2 bg-[#002FA7] text-white text-sm font-semibold cursor-default">
                  {report.landing_page.cta}
                </button>
              )}
            </div>
          )}

          {Array.isArray(report.validation_tweets) && report.validation_tweets.length > 0 && (
            <div>
              <span className="text-xs font-bold uppercase tracking-wider text-zinc-500">Validation Tweets (post today to test demand)</span>
              <div className="mt-1 space-y-2">
                {report.validation_tweets.map((t, i) => (
                  <div key={`tweet-${i}-${(t || "").slice(0,20)}`} className="p-2 bg-sky-500/10 border border-sky-500/30 text-sm flex items-start gap-2">
                    <span className="text-sky-600 font-bold flex-shrink-0">𝕏</span>
                    <div className="flex-1">
                      <p className="text-zinc-800">{t}</p>
                      <button
                        onClick={() => navigator.clipboard.writeText(t)}
                        className="text-xs text-[#002FA7] hover:underline mt-1"
                        data-testid={`copy-tweet-${i}`}
                      >
                        Copy
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {report.biggest_risk && (
            <div className="p-3 bg-red-500/10 border border-red-500/30">
              <span className="text-xs font-bold uppercase tracking-wider text-red-700">⚠️ Biggest Risk</span>
              <p className="text-sm text-red-700 mt-1">{report.biggest_risk}</p>
            </div>
          )}

          {generatedAt && (
            <p className="text-xs text-zinc-500 text-right">Generated {formatTimestamp(generatedAt)}</p>
          )}
        </div>
      )}
    </div>
  );
};

// ============================================================
// IDEA VALIDATOR
// ============================================================
const IdeaValidator = ({ onResult, onViewInSignals }) => {
  const [idea, setIdea] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const handleValidate = async () => {
    if (!idea.trim() || idea.trim().length < 5) return;
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const res = await axios.post(`${API}/validate/idea`, { idea: idea.trim() }, { timeout: 120000 });
      setResult(res.data);
      setError(null);
      if (onResult) onResult(res.data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail) {
        setError(detail);
      } else if (err.code === "ECONNABORTED") {
        setError("Request timed out. Claude is processing — refresh the page in 30 seconds to see the result.");
      } else {
        setError("Scoring failed — possible network timeout. Try again in a moment.");
      }
    } finally {
      setLoading(false);
    }
  };

  const ScoreBar = ({ label, score, weight }) => (
    <div className="flex items-center gap-3 text-sm">
      <span className="w-32 sm:w-36 text-zinc-600 text-[11px] font-mono-data uppercase tracking-[0.15em] font-semibold flex-shrink-0">{label} <span className="text-zinc-500">({weight})</span></span>
      <div className="flex-1 h-2 bg-zinc-100 rounded-full overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-500"
          style={{
            width: `${score}%`,
            background: score >= 70 ? "#10b981" : score >= 50 ? "#f59e0b" : "#ef4444",
          }}
        />
      </div>
      <span className="w-8 text-right font-mono-data text-sm font-bold text-zinc-900 tabular-nums">{score}</span>
    </div>
  );

  return (
    <Card className="col-span-full">
      <div className="space-y-4">
        <div>
          <h3 className="text-lg font-bold text-zinc-900 flex items-center gap-2">
            <MagnifyingGlass size={20} weight="bold" />
            Validate an Idea
          </h3>
          <p className="text-xs text-zinc-500 mt-0.5">Score any business idea against our real Reddit + Fiverr + Upwork + YouTube + ProductHunt + G2 + Trends data</p>
        </div>

        <div className="flex flex-col sm:flex-row gap-2">
          <input
            type="text"
            value={idea}
            onChange={(e) => setIdea(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && !loading && handleValidate()}
            placeholder="Paste any business idea to score it..."
            className="flex-1 px-4 py-2.5 border border-zinc-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-300 focus:border-violet-400 transition-all"
            disabled={loading}
            data-testid="idea-input"
          />
          <button
            onClick={handleValidate}
            disabled={loading || idea.trim().length < 5}
            className="px-5 py-2.5 bg-violet-600 hover:bg-violet-500 text-white font-mono-data font-bold uppercase tracking-[0.15em] text-xs disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2 whitespace-nowrap rounded-full shadow-[0_4px_18px_-6px_rgba(124,58,237,0.5)]"
            data-testid="score-idea-btn"
          >
            {loading ? (
              <><SpinnerGap size={16} className="animate-spin" /> Scoring...</>
            ) : (
              <><Target size={16} weight="bold" /> Score This Idea</>
            )}
          </button>
        </div>

        {error && !result && (
          <div className="flex items-center gap-2 px-3 py-2 bg-red-500/10 text-red-700 text-sm rounded-lg" data-testid="idea-error">
            <Warning size={16} /> {error}
          </div>
        )}

        {result && (
          <div className="border border-violet-200 rounded-2xl p-5 sm:p-6 space-y-5 bg-white shadow-[0_4px_24px_-12px_rgba(124,58,237,0.25)]" data-testid="idea-result">
            {/* Header */}
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-2">
                  <Badge variant={result.verdict === "HOME RUN" ? "success" : result.verdict === "PROMISING" ? "strong" : result.verdict === "LUKEWARM" ? "moderate" : "weak"}>
                    {result.verdict}
                  </Badge>
                  {result.consultant_replacement === 1 && <Badge variant="consultant">CONSULTANT</Badge>}
                  <TrendIndicator direction={result.trend_direction} showLabel />
                  <span className="text-[10px] font-mono-data font-semibold text-violet-700 bg-violet-50 border border-violet-200 px-2 py-0.5 rounded-full uppercase tracking-[0.15em]">Manual Idea</span>
                </div>
                <p className="text-base font-semibold text-zinc-900 leading-relaxed whitespace-pre-wrap break-words max-h-72 overflow-y-auto pr-1">
                  {result.problem}
                </p>
              </div>
              <div className="flex flex-col items-center justify-center px-4 py-3 rounded-2xl bg-zinc-50 border border-zinc-200 flex-shrink-0">
                <div className={`text-4xl font-black tabular-nums ${result.weighted_total >= 70 ? "text-emerald-600" : result.weighted_total >= 50 ? "text-violet-600" : "text-red-500"}`}>
                  {Math.round(result.weighted_total || 0)}
                </div>
                <div className="text-[10px] font-mono-data text-zinc-500 uppercase tracking-[0.18em] mt-0.5">Score</div>
              </div>
            </div>

            {/* Score Bars */}
            <div className="space-y-2.5 pt-1">
              <ScoreBar label="Demand" score={Math.round(result.demand_score || 0)} weight="30%" />
              <ScoreBar label="Competition" score={Math.round(result.competition_score || 0)} weight="25%" />
              <ScoreBar label="Automation" score={Math.round(result.automation_score || 0)} weight="20%" />
              <ScoreBar label="Revenue" score={Math.round(result.revenue_score || 0)} weight="15%" />
              <ScoreBar label="Speed" score={Math.round(result.speed_score || 0)} weight="10%" />
            </div>

            {/* Data Matches */}
            {result.matched_data && (
              <div className="flex flex-wrap gap-3 text-sm pt-3 border-t border-zinc-100">
                <div className="flex items-center gap-1.5 px-3 py-1.5 bg-zinc-50 border border-zinc-200 rounded-full text-zinc-700">
                  <Database size={14} className="text-violet-600" />
                  <span className="font-bold tabular-nums">{result.matched_data.reddit_matches}</span>
                  <span className="text-zinc-500 text-xs">Reddit</span>
                </div>
                <div className="flex items-center gap-1.5 px-3 py-1.5 bg-zinc-50 border border-zinc-200 rounded-full text-zinc-700">
                  <Lightning size={14} className="text-violet-600" />
                  <span className="font-bold tabular-nums">{result.matched_data.fiverr_matches}</span>
                  <span className="text-zinc-500 text-xs">Fiverr</span>
                </div>
                <div className="flex items-center gap-1.5 px-3 py-1.5 bg-zinc-50 border border-zinc-200 rounded-full text-zinc-700">
                  <Crosshair size={14} className="text-violet-600" />
                  <span className="font-bold tabular-nums">{result.matched_data.freelancer_matches}</span>
                  <span className="text-zinc-500 text-xs">Freelancer</span>
                </div>
              </div>
            )}

            {/* Reasoning */}
            {result.reasoning && (
              <div className="border-t border-zinc-100 pt-4">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-[10px] font-mono-data font-semibold text-zinc-500 uppercase tracking-[0.18em]">AI Reasoning</span>
                </div>
                <p className="text-sm text-zinc-700 leading-relaxed">
                  {result.reasoning}
                </p>
              </div>
            )}

            {/* Saved confirmation + actions */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pt-4 border-t border-zinc-100">
              <div className="flex items-center gap-2 text-sm text-emerald-700">
                <CheckCircle size={16} weight="fill" />
                <span>
                  Saved to your <span className="font-semibold text-zinc-900">Signals feed</span> as a <span className="font-mono-data text-[11px] font-semibold text-violet-700 bg-violet-50 border border-violet-200 px-1.5 py-0.5 rounded">My Idea</span>
                </span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => { setResult(null); setIdea(""); }}
                  className="px-4 py-2 text-xs font-semibold text-zinc-700 bg-zinc-50 border border-zinc-200 rounded-full hover:bg-zinc-100 transition-all"
                  data-testid="validate-another-btn"
                >
                  Validate another
                </button>
                <button
                  onClick={() => onViewInSignals && onViewInSignals(result?.id)}
                  className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-bold text-white bg-violet-600 hover:bg-violet-500 rounded-full shadow-[0_4px_18px_-6px_rgba(124,58,237,0.5)] transition-all"
                  data-testid="view-in-signals-btn"
                >
                  View in Signals <ArrowRight size={12} weight="bold" />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Card>
  );
};

// ============================================================
// GOOGLE TRENDS (Passive supplementary data)
// ============================================================
const GoogleTrendsWidget = ({ trends }) => {
  const [expanded, setExpanded] = useState(false);
  const [scoringActive, setScoringActive] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [sortMode, setSortMode] = useState("up"); // 'up' | 'all' | 'down'

  useEffect(() => {
    axios.get(`${API}/google-trends/scoring`).then(res => setScoringActive(res.data.active)).catch(() => {});
  }, []);

  const handleToggle = async () => {
    setToggling(true);
    try {
      const res = await axios.post(`${API}/google-trends/scoring/toggle`);
      setScoringActive(res.data.active);
    } catch (err) {
      console.warn("Trends scoring toggle failed:", err?.response?.status || err?.message);
    }
    setToggling(false);
  };
  
  if (!trends || trends.length === 0) {
    return (
      <Card className="col-span-full">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h3 className="text-base sm:text-lg font-bold text-zinc-900 flex items-center gap-2">
              <ChartBar size={20} weight="bold" />
              Google Trends
              {scoringActive ? (
                <span className="text-[10px] sm:text-xs font-semibold text-emerald-700 bg-emerald-500/10 px-2 py-0.5 rounded">ACTIVE</span>
              ) : (
                <span className="text-[10px] sm:text-xs font-normal text-zinc-500 bg-white px-2 py-0.5 rounded">COLLECTING</span>
              )}
            </h3>
            <p className="text-xs text-zinc-500 mt-0.5">Search volume data collecting in background.</p>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-zinc-500">Use in scoring</span>
            <button
              onClick={handleToggle}
              disabled={toggling}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${scoringActive ? "bg-emerald-500" : "bg-zinc-300"} ${toggling ? "opacity-50" : ""}`}
              data-testid="gtrends-toggle-btn"
              title={scoringActive ? "Click to deactivate" : "Click to activate"}
            >
              <span className={`inline-block h-4 w-4 rounded-full bg-white transition-transform shadow-sm ${scoringActive ? "translate-x-6" : "translate-x-1"}`} />
            </button>
          </div>
        </div>
      </Card>
    );
  }

  // Sort + filter logic
  const sortedFiltered = (() => {
    const arr = Array.isArray(trends) ? [...trends] : [];
    if (sortMode === "up") {
      return arr.filter(t => (t.growth_pct ?? 0) > 0).sort((a, b) => (b.growth_pct ?? 0) - (a.growth_pct ?? 0));
    }
    if (sortMode === "down") {
      return arr.filter(t => (t.growth_pct ?? 0) < 0).sort((a, b) => (a.growth_pct ?? 0) - (b.growth_pct ?? 0));
    }
    return arr.sort((a, b) => (b.growth_pct ?? 0) - (a.growth_pct ?? 0));
  })();
  const upCount = (trends || []).filter(t => (t.growth_pct ?? 0) > 0).length;
  const downCount = (trends || []).filter(t => (t.growth_pct ?? 0) < 0).length;
  const displayed = expanded ? sortedFiltered : sortedFiltered.slice(0, 8);

  return (
    <Card className="col-span-full">
      <div className="space-y-3">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
          <div>
            <h3 className="text-base sm:text-lg font-bold text-zinc-900 flex items-center gap-2">
              <ChartBar size={20} weight="bold" />
              Google Trends
              {scoringActive ? (
                <span className="text-[10px] sm:text-xs font-semibold text-emerald-700 bg-emerald-500/10 px-2 py-0.5 rounded" data-testid="gtrends-active-badge">ACTIVE</span>
              ) : (
                <span className="text-[10px] sm:text-xs font-normal text-zinc-500 bg-white px-2 py-0.5 rounded">PASSIVE</span>
              )}
            </h3>
            <p className="text-xs text-zinc-500 mt-0.5">
              {scoringActive 
                ? "Growth data is boosting/penalizing scores" 
                : "Collecting search data — not yet used in scoring"}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-zinc-500">{trends.length} keywords</span>
            <button
              onClick={handleToggle}
              disabled={toggling}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${scoringActive ? "bg-emerald-500" : "bg-zinc-300"} ${toggling ? "opacity-50" : ""}`}
              data-testid="gtrends-toggle-btn"
              title={scoringActive ? "Click to deactivate Google Trends scoring" : "Click to activate Google Trends scoring"}
            >
              <span className={`inline-block h-4 w-4 rounded-full bg-white transition-transform shadow-sm ${scoringActive ? "translate-x-6" : "translate-x-1"}`} />
            </button>
          </div>
        </div>

        {/* Sort/filter chips */}
        <div className="flex items-center gap-1.5 flex-wrap" data-testid="gtrends-sort-chips">
          {[
            { key: "up", label: "Trending up", count: upCount, dot: "bg-emerald-500" },
            { key: "all", label: "All", count: (trends || []).length, dot: "bg-violet-500" },
            { key: "down", label: "Trending down", count: downCount, dot: "bg-red-400" },
          ].map(opt => (
            <button
              key={opt.key}
              onClick={() => { setSortMode(opt.key); setExpanded(false); }}
              className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-mono-data uppercase tracking-[0.14em] border transition-all ${
                sortMode === opt.key
                  ? "bg-zinc-900 text-white border-zinc-900"
                  : "bg-white text-zinc-600 border-zinc-200 hover:border-zinc-300 hover:text-zinc-900"
              }`}
              data-testid={`gtrends-sort-${opt.key}`}
            >
              <span className={`w-1.5 h-1.5 rounded-full ${opt.dot}`}></span>
              {opt.label}
              <span className={`text-[10px] font-bold ${sortMode === opt.key ? "text-white/80" : "text-zinc-500"}`}>{opt.count}</span>
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2.5">
          {displayed.length === 0 && (
            <div className="col-span-full py-8 text-center" data-testid="gtrends-empty-filter">
              <p className="text-sm text-zinc-500">
                {sortMode === "up"
                  ? "No keywords are trending up right now — try \"All\" to see the full list."
                  : sortMode === "down"
                  ? "Nothing trending down — that's actually a good sign."
                  : "No keywords yet."}
              </p>
            </div>
          )}
          {displayed.map((t, i) => {
            const up = t.growth_pct > 20;
            const down = t.growth_pct < -10;
            const growthChip = up
              ? "bg-emerald-50 text-emerald-700 border-emerald-200"
              : down
              ? "bg-red-50 text-red-600 border-red-200"
              : "bg-zinc-50 text-zinc-500 border-zinc-200";
            return (
              <div
                key={t.keyword}
                className="group relative flex flex-col gap-2 px-3.5 py-3 bg-white rounded-xl border border-zinc-200 hover:border-violet-300 hover:shadow-[0_4px_18px_-8px_rgba(124,58,237,0.25)] transition-all"
                data-testid={`gtrend-${i}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <p className="text-[13px] font-semibold text-zinc-900 leading-snug truncate flex-1" title={t.keyword}>
                    {t.keyword}
                  </p>
                  <span className={`text-[10px] font-mono-data font-semibold px-1.5 py-0.5 rounded-full border whitespace-nowrap ${growthChip}`}>
                    {t.growth_pct > 0 ? "+" : ""}{Math.round(t.growth_pct ?? 0)}%
                  </span>
                </div>
                <div className="flex items-end justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    {t.related_queries && t.related_queries.length > 0 ? (
                      <p className="text-[11px] text-zinc-500 truncate" title={t.related_queries.join(", ")}>
                        {t.related_queries.slice(0, 2).join(", ")}
                      </p>
                    ) : (
                      <p className="text-[11px] text-zinc-500 italic">no related queries yet</p>
                    )}
                  </div>
                  <div className="flex items-baseline gap-1 shrink-0">
                    <span className="font-mono-data text-[10px] uppercase tracking-[0.18em] text-zinc-500">vol</span>
                    <span className="text-base font-black text-violet-600 tabular-nums">{t.interest_score ?? 0}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        
        {sortedFiltered.length > 8 && (
          <button
            onClick={() => setExpanded(!expanded)}
            className="text-xs text-zinc-500 hover:text-zinc-700 transition-colors"
            data-testid="gtrends-expand"
          >
            {expanded ? "Show less" : `Show all ${sortedFiltered.length} keywords`}
          </button>
        )}
      </div>
    </Card>
  );
};

const ScanControl = ({ scanStatus, onStartScan, onSyncAnalyze, schedulerInfo }) => {
  const isRunning = scanStatus?.running;
  const nextScan = schedulerInfo?.next_scheduled_scan;

  const formatNextRun = (isoStr) => {
    if (!isoStr) return "Not scheduled";
    const d = new Date(isoStr);
    const now = new Date();
    const diffMs = d - now;
    const diffH = Math.floor(diffMs / 3600000);
    const diffM = Math.floor((diffMs % 3600000) / 60000);
    if (diffMs < 0) return "Overdue";
    if (diffH > 0) return `in ${diffH}h ${diffM}m`;
    return `in ${diffM}m`;
  };

  return (
    <Card className="col-span-full">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <div className="flex items-center gap-3 flex-wrap">
            <h3 className="text-base sm:text-lg font-bold text-zinc-900">Scanner Control</h3>
            {schedulerInfo?.scheduler_running && (
              <span className="inline-flex items-center gap-1.5 px-2 py-0.5 text-[10px] sm:text-xs font-semibold bg-emerald-500/10 text-emerald-700" data-testid="scheduler-badge">
                <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse" />
                Auto: 02 (Apify) / 06 / 12 / 18 UTC
              </span>
            )}
          </div>
          {isRunning ? (
            <div className="flex items-center gap-2 mt-1">
              <SpinnerGap size={16} className="animate-spin text-[#002FA7]" />
              <span className="text-sm text-zinc-500">{scanStatus.current_task}</span>
              {scanStatus.total > 0 && (
                <span className="text-xs text-zinc-500">
                  ({scanStatus.progress}/{scanStatus.total})
                </span>
              )}
            </div>
          ) : scanStatus.last_error ? (
            <div className="flex items-start gap-2 mt-1" data-testid="scan-error-banner">
              <Warning size={16} className="text-red-500 flex-shrink-0 mt-0.5" />
              <span className="text-sm text-red-600 break-words">{scanStatus.last_error}</span>
            </div>
          ) : (
            <p className="text-xs sm:text-sm text-zinc-500 mt-1">
              {nextScan ? `Next auto-scan ${formatNextRun(nextScan)}` : "Scan Reddit + Freelancer + Fiverr for opportunities"}
            </p>
          )}
        </div>
        <div className="grid grid-cols-2 sm:flex gap-2">
          <Button 
            variant="secondary" 
            onClick={() => onStartScan("reddit")}
            disabled={isRunning}
            className="text-xs sm:text-sm"
            data-testid="scan-reddit-btn"
          >
            Reddit
          </Button>
          <Button 
            variant="secondary" 
            onClick={() => onStartScan("freelancer")}
            disabled={isRunning}
            className="text-xs sm:text-sm"
            data-testid="scan-freelancer-btn"
          >
            Freelancer
          </Button>
          <Button 
            variant="secondary" 
            onClick={() => onStartScan("ai_pulse")}
            disabled={isRunning}
            className="text-xs sm:text-sm bg-violet-100 text-violet-700 hover:bg-violet-200"
            data-testid="scan-ai-pulse-btn"
            title="Scan IndieHackers + ProductHunt + GitHub Trending for AI signals"
          >
            <Sparkle size={14} className="mr-1" weight="fill" />
            AI Pulse
          </Button>
          <Button 
            variant="secondary" 
            onClick={() => onStartScan("apify_full")}
            disabled={isRunning}
            className="text-xs sm:text-sm bg-gradient-to-r from-emerald-500 to-teal-600 text-white hover:from-emerald-600 hover:to-teal-700"
            data-testid="scan-apify-btn"
            title="Premium scan via Apify — managed actors, richer data"
          >
            🚀 Premium Scan
          </Button>
          <Button 
            variant="primary" 
            onClick={() => onStartScan("full")}
            disabled={isRunning}
            className="text-xs sm:text-sm"
            data-testid="scan-full-btn"
          >
            <Crosshair size={16} className="mr-1" />
            {isRunning ? "Scanning..." : "Full Scan"}
          </Button>
          <Button 
            variant="warning" 
            onClick={onSyncAnalyze}
            disabled={isRunning}
            className="bg-[#7C3AED] text-black hover:bg-yellow-500 text-xs sm:text-sm"
            data-testid="sync-analyze-btn"
          >
            <Robot size={16} className="mr-1" />
            AI Analyze
          </Button>
        </div>
      </div>
    </Card>
  );
};

const LastAIRunCard = () => {
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const fetchIt = async () => {
      try {
        const res = await axios.get(`${API}/analysis/last-run`);
        if (!cancelled) setData(res.data);
      } catch (err) {
        if (!cancelled) console.warn("last-run fetch failed:", err?.response?.status || err?.message);
      }
    };
    fetchIt();
    const id = setInterval(fetchIt, 30000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  if (!data) return null;
  const saved = data.saved || 0;
  const extracted = data.extracted || 0;
  const clustered = data.clustered || 0;
  const scored = data.scored || 0;
  const failedBatches = data.failed_batches || 0;
  const totalBatches = data.total_batches || 0;
  const fallbackUsed = data.fallback_used || 0;
  const discarded = data.discarded_by_clustering || 0;
  const error = data.error;
  const reason = data.reason || "";

  // Tone of the card
  let tone = "border-zinc-200 bg-white";
  let dot = "bg-zinc-400";
  if (error) { tone = "border-red-500/30 bg-red-500/10"; dot = "bg-red-500"; }
  else if (saved === 0) { tone = "border-violet-200 bg-violet-600/10"; dot = "bg-violet-600"; }
  else { tone = "border-emerald-500/30 bg-emerald-500/10"; dot = "bg-emerald-500"; }

  return (
    <Card className={`border ${tone}`} data-testid="last-ai-run-card">
      <div className="flex items-start gap-3">
        <span className={`w-2.5 h-2.5 rounded-full mt-1.5 ${dot} flex-shrink-0`} />
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-bold text-zinc-900 uppercase tracking-wider mb-1">Last AI Analysis</h3>
          {error ? (
            <p className="text-sm text-red-700 break-words" data-testid="last-ai-run-error">{error}</p>
          ) : (
            <p className="text-sm text-zinc-700">{reason}</p>
          )}
          <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
            <div><span className="font-semibold">Extracted:</span> {extracted}</div>
            <div><span className="font-semibold">Clustered:</span> {clustered}</div>
            <div><span className="font-semibold">Saved as new:</span> {saved}</div>
            <div><span className="font-semibold">Scored:</span> {scored}</div>
            {totalBatches > 0 && (
              <div className="col-span-2">
                <span className="font-semibold">Batches:</span> {totalBatches - failedBatches}/{totalBatches} succeeded
                {fallbackUsed > 0 && <span className="ml-2 text-amber-700">(GPT-5 fallback × {fallbackUsed})</span>}
              </div>
            )}
            {discarded > 0 && (
              <div className="col-span-2 sm:col-span-2">
                <span className="font-semibold">Filtered out:</span> {discarded} candidates (frequency &lt; 3)
              </div>
            )}
            {data.finished_at && (
              <div className="col-span-2 sm:col-span-4 text-zinc-500 mt-1">
                Last run: {formatTimestamp(data.finished_at)}
              </div>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
};

const SourcesBar = ({ activeSource, onSelectSource }) => {
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const fetchIt = async () => {
      try {
        const res = await axios.get(`${API}/stats/sources`);
        if (!cancelled) setData(res.data);
      } catch {
        // ignore
      }
    };
    fetchIt();
    const id = setInterval(fetchIt, 60000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  if (!data || !data.total) return null;

  const PALETTE = {
    reddit:      { color: "bg-violet-600",  label: "Reddit" },
    hackernews:  { color: "bg-violet-700",  label: "Hacker News" },
    freelancer:  { color: "bg-rose-500",    label: "Freelancer" },
    fiverr:      { color: "bg-emerald-500", label: "Fiverr" },
    upwork:      { color: "bg-green-600",   label: "Upwork" },
    youtube:     { color: "bg-red-500",     label: "YouTube" },
    producthunt: { color: "bg-orange-500",  label: "ProductHunt" },
    g2:          { color: "bg-amber-500",   label: "G2 Reviews" },
    trends:      { color: "bg-cyan-500",    label: "Google Trends" },
    remoteok:    { color: "bg-sky-500",     label: "RemoteOK" },
    manual:      { color: "bg-violet-500",  label: "My Ideas" },
    other:       { color: "bg-zinc-400",    label: "Other" },
  };

  const items = Object.entries(data.by_source)
    .filter(([, count]) => count > 0)
    .map(([key, count]) => ({
      key,
      count,
      pct: (count / data.total) * 100,
      ...PALETTE[key],
    }))
    .sort((a, b) => b.count - a.count);

  if (items.length === 0) return null;

  const handleClick = (key) => {
    if (!onSelectSource) return;
    onSelectSource(activeSource === key ? null : key);
  };

  return (
    <Card data-testid="sources-bar-card">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <h3 className="text-sm font-bold text-zinc-900 uppercase tracking-wider">
          Opportunities by Source <span className="text-zinc-500 font-normal normal-case">({data.total} total)</span>
        </h3>
        {activeSource && (
          <button
            onClick={() => onSelectSource(null)}
            className="text-xs font-semibold text-[#002FA7] hover:underline"
            data-testid="sources-clear-filter"
          >
            Clear filter ({PALETTE[activeSource]?.label || activeSource}) ✕
          </button>
        )}
      </div>
      <div className="flex h-3 w-full overflow-hidden rounded-sm bg-white" data-testid="sources-bar">
        {items.map((it) => {
          const dim = activeSource && activeSource !== it.key;
          return (
            <button
              key={it.key}
              onClick={() => handleClick(it.key)}
              className={`${it.color} transition-all hover:opacity-80 ${dim ? "opacity-30" : "opacity-100"}`}
              style={{ width: `${it.pct}%` }}
              title={`${it.label}: ${it.count} (${it.pct.toFixed(1)}%) — click to filter`}
              data-testid={`source-segment-${it.key}`}
            />
          );
        })}
      </div>
      <div className="mt-3 flex flex-wrap gap-x-4 gap-y-2 text-xs">
        {items.map((it) => {
          const isActive = activeSource === it.key;
          return (
            <button
              key={it.key}
              onClick={() => handleClick(it.key)}
              className={`flex items-center gap-1.5 px-1.5 py-0.5 rounded transition-all hover:bg-white ${isActive ? "bg-white ring-1 ring-zinc-300" : ""}`}
              data-testid={`source-legend-${it.key}`}
            >
              <span className={`w-2.5 h-2.5 rounded-sm ${it.color}`} />
              <span className="font-semibold text-zinc-700">{it.label}</span>
              <span className="text-zinc-500">{it.count} · {it.pct.toFixed(0)}%</span>
            </button>
          );
        })}
      </div>
    </Card>
  );
};

const Top5Report = ({ opportunities }) => {
  if (!opportunities || opportunities.length === 0) {
    return (
      <Card className="col-span-full">
        <h3 className="text-lg font-bold text-zinc-900 mb-4 flex items-center gap-2">
          <Lightning size={20} className="text-[#7C3AED]" />
          Weekly Top 5
        </h3>
        <p className="text-zinc-500 text-center py-8">No scored opportunities yet. Run a full scan to discover opportunities.</p>
      </Card>
    );
  }

  return (
    <Card className="col-span-full">
      <h3 className="text-base sm:text-lg font-bold text-zinc-900 mb-4 flex items-center gap-2">
        <Lightning size={20} className="text-[#7C3AED]" />
        Weekly Top 5
      </h3>
      <div className="overflow-x-auto -mx-6 px-6">
        <table className="w-full min-w-[600px]">
          <thead>
            <tr className="border-b-2 border-zinc-200 text-left">
              <th className="pb-2 text-xs font-bold uppercase tracking-wider text-zinc-500">#</th>
              <th className="pb-2 text-xs font-bold uppercase tracking-wider text-zinc-500">Opportunity</th>
              <th className="pb-2 text-xs font-bold uppercase tracking-wider text-zinc-500">Score</th>
              <th className="pb-2 text-xs font-bold uppercase tracking-wider text-zinc-500">Trend</th>
              <th className="pb-2 text-xs font-bold uppercase tracking-wider text-zinc-500">Signal</th>
              <th className="pb-2 text-xs font-bold uppercase tracking-wider text-zinc-500">Action</th>
            </tr>
          </thead>
          <tbody>
            {opportunities.map((opp, idx) => (
              <tr key={opp.id} className="border-b border-zinc-200">
                <td className="py-3 font-bold text-zinc-500">{idx + 1}</td>
                <td className="py-3 pr-4 max-w-md">
                  <p className="font-semibold text-zinc-900 line-clamp-1">{opp.problem}</p>
                  <p className="text-sm text-zinc-500">{opp.user_type}</p>
                </td>
                <td className="py-3">
                  <span className={`inline-flex items-center justify-center w-10 h-10 font-bold text-white ${opp.weighted_total >= 85 ? 'bg-emerald-500' : opp.weighted_total >= 70 ? 'bg-[#7C3AED] text-black' : 'bg-zinc-400'}`}>
                    {Math.round(opp.weighted_total || 0)}
                  </span>
                </td>
                <td className="py-3">
                  <TrendIndicator direction={opp.trend_direction} />
                </td>
                <td className="py-3">
                  <Badge variant={opp.consultant_replacement ? "success" : "default"}>
                    {opp.consultant_replacement ? "CONSULTANT" : opp.signal_strength}
                  </Badge>
                </td>
                <td className="py-3">
                  <Badge variant={opp.weighted_total >= 85 ? "success" : opp.weighted_total >= 70 ? "warning" : "default"}>
                    {opp.weighted_total >= 70 ? "VALIDATE" : "RESEARCH"}
                  </Badge>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
};

const SettingsPanel = ({ settings, onClose }) => {
  const [redditId, setRedditId] = useState("");
  const [redditSecret, setRedditSecret] = useState("");

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
        <div className="border-b border-zinc-200 p-6 flex items-center justify-between">
          <h2 className="text-xl font-black tracking-tight">Settings</h2>
          <button onClick={onClose} className="p-2 hover:bg-white" data-testid="close-settings-btn">
            <X size={24} />
          </button>
        </div>
        
        <div className="p-6 space-y-6">
          <div>
            <h3 className="text-sm font-bold uppercase tracking-wider text-zinc-500 mb-3">System Status</h3>
            <div className="space-y-2">
              <div className="flex items-center justify-between py-2 border-b border-zinc-200">
                <span className="text-zinc-500">Reddit API</span>
                {settings?.reddit_configured ? (
                  <Badge variant="success">Configured</Badge>
                ) : (
                  <Badge variant="danger">Not Configured</Badge>
                )}
              </div>
              <div className="flex items-center justify-between py-2 border-b border-zinc-200">
                <span className="text-zinc-500">LLM (Claude)</span>
                {settings?.llm_configured ? (
                  <Badge variant="success">Configured</Badge>
                ) : (
                  <Badge variant="danger">Not Configured</Badge>
                )}
              </div>
              <div className="flex items-center justify-between py-2 border-b border-zinc-200">
                <span className="text-zinc-500">Target Subreddits</span>
                <span className="font-semibold">{settings?.subreddits_count || 0}</span>
              </div>
              <div className="flex items-center justify-between py-2 border-b border-zinc-200">
                <span className="text-zinc-500">Freelancer Categories</span>
                <span className="font-semibold">{settings?.freelancer_categories_count || 0}</span>
              </div>
              <div className="flex items-center justify-between py-2">
                <span className="text-zinc-500">Score Threshold</span>
                <span className="font-semibold">{settings?.score_threshold || 70}</span>
              </div>
            </div>
          </div>

          <div>
            <h3 className="text-sm font-bold uppercase tracking-wider text-zinc-500 mb-3">Reddit API Credentials</h3>
            <p className="text-sm text-zinc-500 mb-3">
              Get credentials at <a href="https://reddit.com/prefs/apps" target="_blank" rel="noopener noreferrer" className="text-[#002FA7] underline">reddit.com/prefs/apps</a>
            </p>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-zinc-500 block mb-1">Client ID</label>
                <input 
                  type="text"
                  value={redditId}
                  onChange={(e) => setRedditId(e.target.value)}
                  placeholder="Enter Reddit Client ID"
                  className="w-full px-3 py-2 border border-zinc-300 text-sm focus:ring-2 focus:ring-[#002FA7] focus:border-[#002FA7] outline-none"
                  data-testid="reddit-client-id-input"
                />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-zinc-500 block mb-1">Client Secret</label>
                <input 
                  type="password"
                  value={redditSecret}
                  onChange={(e) => setRedditSecret(e.target.value)}
                  placeholder="Enter Reddit Client Secret"
                  className="w-full px-3 py-2 border border-zinc-300 text-sm focus:ring-2 focus:ring-[#002FA7] focus:border-[#002FA7] outline-none"
                  data-testid="reddit-client-secret-input"
                />
              </div>
              <p className="text-xs text-zinc-500">
                Note: Update the backend .env file to persist credentials.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================================
// TIMELINE VIEW
// ============================================================
const TimelineView = () => {
  const [timeline, setTimeline] = useState([]);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/opportunities/timeline?days=${days}`, { timeout: 12000 })
      .then(res => setTimeline(res.data))
      .catch((err) => {
        console.warn("Timeline fetch failed:", err?.message);
        setTimeline([]);
      })
      .finally(() => setLoading(false));
  }, [days]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <SpinnerGap size={32} className="animate-spin text-zinc-500" />
      </div>
    );
  }

  if (timeline.length === 0) {
    return (
      <Card className="text-center py-12" data-testid="timeline-empty">
        <CalendarBlank size={48} className="text-zinc-700 mx-auto mb-4" />
        <h3 className="text-lg font-bold text-zinc-500 mb-2">No Activity Yet</h3>
        <p className="text-zinc-500">Run your first scan to start tracking daily changes here.</p>
      </Card>
    );
  }

  return (
    <div className="space-y-6" data-testid="timeline-view">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-bold uppercase tracking-widest text-zinc-500">Daily Feed</h3>
        <select
          value={days}
          onChange={e => setDays(Number(e.target.value))}
          className="text-xs border border-zinc-200 bg-white px-2 py-1 text-zinc-500 focus:outline-none"
          data-testid="timeline-days-select"
        >
          <option value={7}>Last 7 days</option>
          <option value={14}>Last 14 days</option>
          <option value={30}>Last 30 days</option>
        </select>
      </div>

      {timeline.map((day, idx) => (
        <div key={day.date} className="relative" data-testid={`timeline-day-${day.date}`}>
          {/* Timeline connector */}
          {idx < timeline.length - 1 && (
            <div className="absolute left-4 sm:left-5 top-10 sm:top-12 bottom-0 w-px bg-zinc-50" />
          )}

          {/* Day header */}
          <div className="flex items-center gap-2 sm:gap-3 mb-3">
            <div className={`w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center flex-shrink-0 ${day.date_label === "TODAY" ? "bg-[#002FA7] text-white" : "bg-white text-zinc-500"}`}>
              <CalendarBlank size={16} weight="bold" />
            </div>
            <div className="min-w-0">
              <span className="text-xs sm:text-sm font-black text-zinc-900 tracking-tight">{day.date_label}</span>
              <span className="text-[10px] sm:text-xs text-zinc-500 ml-1.5 sm:ml-2">{day.date_display}</span>
            </div>
          </div>

          <div className="ml-10 sm:ml-[52px] space-y-3">
            {/* Scan stats */}
            {day.scan_stats.scan_count > 0 && (
              <div className="flex items-center gap-2 sm:gap-4 text-[10px] sm:text-xs text-zinc-500 bg-zinc-50 border border-zinc-200 px-2 sm:px-3 py-1.5 sm:py-2" data-testid={`timeline-scan-${day.date}`}>
                <span className="font-semibold text-zinc-700">{day.scan_stats.scan_count} scan{day.scan_stats.scan_count > 1 ? "s" : ""}</span>
                <span>{day.scan_stats.total_found} found</span>
                <span>{day.scan_stats.total_saved} saved</span>
              </div>
            )}

            {/* New opportunities */}
            {day.new_opportunities.length > 0 && (
              <div className="space-y-1.5" data-testid={`timeline-new-${day.date}`}>
                <span className="text-[10px] font-bold uppercase tracking-widest text-violet-700">New Opportunities</span>
                {day.new_opportunities.map(opp => (
                  <div key={opp.id} className="flex items-center gap-2 sm:gap-3 px-2 sm:px-3 py-1.5 sm:py-2 bg-violet-600/10 border border-amber-100" data-testid={`timeline-new-opp-${opp.id}`}>
                    <Star size={12} weight="fill" className="text-violet-600 flex-shrink-0" />
                    <p className="text-xs sm:text-sm text-zinc-800 flex-1 min-w-0 truncate">{opp.problem}</p>
                    <div className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0">
                      {opp.weighted_total != null && (
                        <span className={`text-[10px] sm:text-xs font-bold px-1 sm:px-1.5 py-0.5 ${opp.weighted_total >= 70 ? "bg-emerald-100 text-emerald-700" : "bg-white text-zinc-500"}`}>
                          {Math.round(opp.weighted_total)}
                        </span>
                      )}
                      {opp.verdict && (
                        <span className="text-[9px] sm:text-[10px] font-semibold text-zinc-500 uppercase hidden sm:inline">{opp.verdict}</span>
                      )}
                      {opp.consultant_replacement === 1 && (
                        <span className="text-[9px] sm:text-[10px] font-bold text-[#002FA7]">CONSULT</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Score changes */}
            {day.score_changes.length > 0 && (
              <div className="space-y-1.5" data-testid={`timeline-changes-${day.date}`}>
                <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">Score Changes</span>
                {day.score_changes.map(sc => (
                  <div
                    key={sc.opportunity_id}
                    className={`flex items-center gap-2 sm:gap-3 px-2 sm:px-3 py-1.5 sm:py-2 border ${sc.change > 0 ? "bg-emerald-500/10 border-emerald-100" : "bg-red-500/10 border-red-100"}`}
                    data-testid={`timeline-change-${sc.opportunity_id}`}
                  >
                    {sc.change > 0 ? (
                      <ArrowUp size={12} weight="bold" className="text-emerald-600 flex-shrink-0" />
                    ) : (
                      <ArrowDown size={12} weight="bold" className="text-red-600 flex-shrink-0" />
                    )}
                    <p className="text-xs sm:text-sm text-zinc-800 flex-1 min-w-0 truncate">{sc.problem}</p>
                    <div className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0">
                      <span className={`text-[10px] sm:text-xs font-bold ${sc.change > 0 ? "text-emerald-700" : "text-red-700"}`}>
                        {sc.change > 0 ? "+" : ""}{sc.change}
                      </span>
                      <span className="text-[9px] sm:text-[10px] text-zinc-500">
                        {Math.round(sc.prev_score)} → {Math.round(sc.today_score)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Killed */}
            {day.killed.length > 0 && (
              <div className="space-y-1.5" data-testid={`timeline-killed-${day.date}`}>
                <span className="text-[10px] font-bold uppercase tracking-widest text-red-400">Killed</span>
                {day.killed.map(k => (
                  <div key={k.id} className="flex items-center gap-2 sm:gap-3 px-2 sm:px-3 py-1.5 sm:py-2 bg-white border border-zinc-200 opacity-60" data-testid={`timeline-killed-opp-${k.id}`}>
                    <Skull size={12} weight="bold" className="text-zinc-500 flex-shrink-0" />
                    <p className="text-xs sm:text-sm text-zinc-500 flex-1 min-w-0 truncate line-through">{k.problem}</p>
                    {k.weighted_total != null && (
                      <span className="text-[10px] sm:text-xs text-zinc-500 flex-shrink-0">{Math.round(k.weighted_total)}</span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

// ============================================================
// AI PULSE VIEW — moved to /components/AIPulseView.js
// ============================================================


// ============================================================
// MAIN APP
// ============================================================
function App() {
  const { user, ready: authReady } = useAuth();
  const [stats, setStats] = useState(null);
  const [opportunities, setOpportunities] = useState([]);
  const [top5, setTop5] = useState([]);
  const [scanStatus, setScanStatus] = useState({ running: false });
  const [settings, setSettings] = useState(null);
  const [googleTrends, setGoogleTrends] = useState([]);
  const [selectedOpp, setSelectedOpp] = useState(null);
  const [showSettings, setShowSettings] = useState(false);

  // Demo-mode: ProArbitrageDiscoveryBanner can dispatch a custom event to
  // open a specific opp DIRECTLY in the Pro Arbitrage tab (1-click demo path).
  // We mutate a synthetic `__open_in_arbitrage` marker so OpportunityDetail
  // knows to render with initialMode='arbitrage'.
  useEffect(() => {
    const onDemoOpen = (e) => {
      const oppId = e?.detail?.opp_id;
      if (!oppId) return;
      const match = opportunities.find((o) => o.id === oppId);
      if (match) {
        setSelectedOpp({ ...match, __open_in_arbitrage: true });
      } else {
        // Synthesize a minimal opp shape — arbitrage endpoint takes id only
        setSelectedOpp({ id: oppId, problem: "Loading…", __open_in_arbitrage: true });
      }
    };
    window.addEventListener("pp:open-arbitrage-demo", onDemoOpen);
    return () => window.removeEventListener("pp:open-arbitrage-demo", onDemoOpen);
  }, [opportunities]);
  // Cards anywhere can fire pp:open-country-fit to jump to the Best Country page.
  useEffect(() => {
    const go = () => setActiveTab("country_fit");
    window.addEventListener("pp:open-country-fit", go);
    return () => window.removeEventListener("pp:open-country-fit", go);
  }, []);
  const [activeTab, setActiveTab] = useState(() => {
    const p = window.location.pathname;
    if (p.startsWith("/pricing")) return "pricing";
    if (p.startsWith("/account")) return "account";
    if (p.startsWith("/evaluate")) return "evaluate";
    if (p.startsWith("/best-country")) return "country_fit";
    if (p.startsWith("/ask")) return "copilot";
    if (p.startsWith("/commerce")) return "commerce";
    if (p.startsWith("/dashboard") || p.startsWith("/signals") || p.startsWith("/trending")) return "dashboard";
    // Anonymous visitors land on the marketing page; once they enter the app
    // (via a CTA or the header nav) they get Discover — the led product —
    // not the legacy scanner Dashboard.
    const token = typeof window !== "undefined" ? localStorage.getItem("p2p_token") : null;
    return token ? "discover" : "landing";
  });
  const [discoverMode, setDiscoverMode] = useState("vc");
  const [evaluateMode, setEvaluateMode] = useState("vc");
  const [guideTarget, setGuideTarget] = useState(null);
  // Any feature header can fire pp:open-guide{detail:{feature}} to jump to the
  // Guide tab pre-scrolled to that feature's section.
  useEffect(() => {
    const go = (e) => { setGuideTarget((e && e.detail && e.detail.feature) || null); setActiveTab("guide"); };
    window.addEventListener("pp:open-guide", go);
    return () => window.removeEventListener("pp:open-guide", go);
  }, []);
  // Public shareable opp view at /opp/{id}
  const [publicOppId, setPublicOppId] = useState(() => {
    const m = (typeof window !== "undefined" ? window.location.pathname : "").match(/^\/opp\/(\d+)/);
    return m ? parseInt(m[1], 10) : null;
  });
  // Public shareable brief view at /brief/{id}
  const [publicBriefId, setPublicBriefId] = useState(() => {
    const m = (typeof window !== "undefined" ? window.location.pathname : "").match(/^\/brief\/(\d+)/);
    return m ? parseInt(m[1], 10) : null;
  });
  useEffect(() => {
    const onPop = () => {
      const path = window.location.pathname;
      const mo = path.match(/^\/opp\/(\d+)/);
      setPublicOppId(mo ? parseInt(mo[1], 10) : null);
      const mb = path.match(/^\/brief\/(\d+)/);
      setPublicBriefId(mb ? parseInt(mb[1], 10) : null);
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [sourceFilter, setSourceFilter] = useState(null); // null = all sources
  const [nicheFilter, setNicheFilter] = useState(null);   // null = all niches
  const [validatedOnly, setValidatedOnly] = useState(false); // show only ≥2-source opps
  const [trendingUp, setTrendingUp] = useState(false);       // show only Google-Trends rising opps
  const [niches, setNiches] = useState([]);               // [{key,label,is_ai}]
  const [myNiches, setMyNiches] = useState([]);           // user's starred niche keys
  const [personalize, setPersonalize] = useState(true);   // sort starred niches first
  const [authOpen, setAuthOpen] = useState(false);
  const [authMode, setAuthMode] = useState("login");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [starred, setStarred] = useState([]);
  // Founder Profile (3-question onboarding) + Fit Score ranking
  const [founderProfile, setFounderProfile] = useState(null);  // {stack, budget, hours_per_week} | null
  const [profileSkipped, setProfileSkipped] = useState(false); // user opted out of personalization
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [fitRanking, setFitRanking] = useState(true);          // rank by fit score when profile exists

  const requireAuth = useCallback(() => {
    if (!user) { setAuthMode("signup"); setAuthOpen(true); return true; }
    return false;
  }, [user]);

  const goToPricing = () => { setActiveTab("pricing"); };

  // Load watchlist for logged-in users
  useEffect(() => {
    if (!user) { setStarred([]); return; }
    axios.get(`${API}/me/watchlist`).then(r => {
      setStarred((r.data.opportunities || []).map(o => o.id));
    }).catch(() => {});
  }, [user]);

  // Load founder profile on login. Auto-open the onboarding modal the first
  // time we see a logged-in user who has neither set a profile nor skipped.
  useEffect(() => {
    if (!user) {
      setFounderProfile(null);
      setProfileSkipped(false);
      setProfileModalOpen(false);
      return;
    }
    const token = localStorage.getItem("p2p_token");
    if (!token) return;
    axios
      .get(`${API}/me/profile`, { headers: { Authorization: `Bearer ${token}` }, timeout: 8000 })
      .then((r) => {
        const d = r.data || {};
        if (d.has_profile && d.profile) {
          setFounderProfile(d.profile);
          setProfileSkipped(false);
        } else if (d.skipped) {
          setFounderProfile(null);
          setProfileSkipped(true);
        } else {
          setFounderProfile(null);
          setProfileSkipped(false);
          setProfileModalOpen(true); // first-time prompt
        }
      })
      .catch(() => {});
  }, [user]);

  const onProfileSaved = useCallback((profile) => {
    if (profile?.skipped) {
      setFounderProfile(null);
      setProfileSkipped(true);
    } else {
      setFounderProfile(profile);
      setProfileSkipped(false);
    }
  }, []);

  const toggleStar = useCallback(async (oppId) => {
    try {
      const r = await axios.post(`${API}/me/watchlist/toggle`, { opportunity_id: oppId });
      setStarred(r.data.watchlist || []);
    } catch (err) {
      console.warn("Failed to toggle watchlist:", err?.response?.status || err?.message);
    }
  }, []);

  const fetchData = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (sourceFilter) { params.set("source", sourceFilter); params.set("limit", "200"); }
      else if (nicheFilter) { params.set("niche", nicheFilter); params.set("limit", "200"); }
      else {
        params.set("limit", "50");
        if (personalize && myNiches.length > 0) params.set("personalize", "1");
      }
      if (validatedOnly) {
        params.set("min_sources", "2");
        if (!params.get("limit") || parseInt(params.get("limit")) < 200) params.set("limit", "200");
      }
      if (trendingUp) {
        params.set("trending_up", "1");
        if (!params.get("limit") || parseInt(params.get("limit")) < 200) params.set("limit", "200");
      }
      // AI-tagged opportunities live exclusively in the AI Pulse tab.
      // Keep the main Signals feed strictly non-AI to avoid duplication.
      params.set("exclude_ai", "1");
      const oppsUrl = `${API}/opportunities?${params.toString()}`;
      const opts = { timeout: 12000 }; // 12s hard timeout — never hang the app on a slow/dead API
      const [statsRes, oppsRes, top5Res, settingsRes, gtrendsRes, proAvailRes] = await Promise.all([
        axios.get(`${API}/stats`, opts),
        axios.get(oppsUrl, opts),
        axios.get(`${API}/top5`, opts),
        axios.get(`${API}/settings`, opts),
        axios.get(`${API}/google-trends`, opts).catch(() => ({ data: [] })),
        axios.get(`${API}/arbitrage/available-opps`, opts).catch(() => ({ data: { opportunity_ids: [] } })),
      ]);
      setStats(statsRes.data);
      // Annotate each opp with __has_pro_arbitrage + __best_country so cards can
      // surface a "Best country" badge linking to the /best-country page.
      const proIds = new Set((proAvailRes.data?.opportunity_ids) || []);
      const countryById = proAvailRes.data?.country_by_id || {};
      setOpportunities((oppsRes.data || []).map(o => ({
        ...o,
        __has_pro_arbitrage: proIds.has(o.id),
        __best_country: countryById[String(o.id)] || null,
      })));
      setTop5(top5Res.data);
      setSettings(settingsRes.data);
      setGoogleTrends(gtrendsRes.data);
    } catch (e) {
      console.error("Error fetching data:", e?.message || e);
    } finally {
      setLoading(false);
    }
  }, [sourceFilter, nicheFilter, personalize, myNiches, validatedOnly, trendingUp]);

  // Load the canonical niche catalog + the user's "My Niches" once on mount.
  useEffect(() => {
    axios.get(`${API}/niches`, { timeout: 8000 })
      .then(r => setNiches(r.data?.niches || []))
      .catch(() => setNiches([]));
    axios.get(`${API}/me/niches`, { timeout: 8000 })
      .then(r => setMyNiches(r.data?.selected_niches || []))
      .catch(() => setMyNiches([]));
  }, []);

  // Persist niche favorites to backend whenever the user toggles them.
  const toggleMyNiche = useCallback(async (key) => {
    const next = myNiches.includes(key)
      ? myNiches.filter(k => k !== key)
      : [...myNiches, key];
    setMyNiches(next); // optimistic
    try {
      await axios.put(`${API}/me/niches`, { selected_niches: next }, { timeout: 8000 });
    } catch (err) {
      console.warn("Failed to save My Niches:", err?.message);
    }
  }, [myNiches]);

  const fetchScanStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/scan/status`);
      setScanStatus(res.data);
      if (res.data.running === false && scanStatus.running === true) {
        fetchData();
      }
    } catch (e) {
      console.error("Error fetching scan status:", e);
    }
  }, [scanStatus.running, fetchData]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    const interval = setInterval(fetchScanStatus, 3000);
    return () => clearInterval(interval);
  }, [fetchScanStatus]);

  const handleStartScan = async (scanType) => {
    try {
      await axios.post(`${API}/scan/start`, { scan_type: scanType });
      setScanStatus({ running: true, current_task: "Starting...", progress: 0, total: 0 });
    } catch (e) {
      console.error("Error starting scan:", e);
      alert(e.response?.data?.detail || "Failed to start scan");
    }
  };

  const handleSyncAnalyze = async () => {
    try {
      setScanStatus({ running: true, current_task: "Running Sync AI Analysis...", progress: 0, total: 0 });
      const response = await axios.post(`${API}/analyze/sync`, {}, { timeout: 300000 }); // 5 min timeout
      if (response.data.success) {
        alert(`AI Analysis Complete!\n\nExtracted: ${response.data.result.extracted || 0}\nClustered: ${response.data.result.clustered || 0}\nSaved: ${response.data.result.saved || 0}\nScored: ${response.data.result.scored || 0}`);
        fetchData();
      } else {
        alert(`Analysis failed: ${response.data.error}`);
      }
    } catch (e) {
      console.error("Error running sync analysis:", e);
      alert(e.response?.data?.detail || e.message || "Analysis failed");
    } finally {
      setScanStatus({ running: false, current_task: "Complete", progress: 0, total: 0 });
    }
  };

  const handleAction = async (oppId, action) => {
    try {
      await axios.post(`${API}/opportunities/${oppId}/action`, { action });
      setOpportunities(prev => prev.filter(o => o.id !== oppId));
      setSelectedOpp(null);
      fetchData();
    } catch (e) {
      console.error("Error performing action:", e);
    }
  };

  // Apply category filters, then optionally re-rank by Fit Score when the
  // user has a founder profile and fit-ranking is enabled.
  const filteredOpportunities = (() => {
    const filtered = opportunities.filter(opp => {
      if (filter === "all") return true;
      if (filter === "validated_ideas") return opp.status === "MANUAL_IDEA";
      if (filter === "homerun") return (opp.weighted_total || 0) >= 85;
      if (filter === "promising") return (opp.weighted_total || 0) >= 70 && (opp.weighted_total || 0) < 85;
      if (filter === "consultant") return opp.consultant_replacement === 1;
      if (filter === "new_today")  return getFreshness(opp) === "new_today";
      if (filter === "fresh_week") return getFreshness(opp) !== null;
      return true;
    });
    if (fitRanking && founderProfile && !founderProfile.skipped && founderProfile.stack) {
      // Annotate with fit_score and sort desc. Stable sort breaks ties by
      // existing weighted_total so we don't shuffle equivalent rows on every render.
      const withFit = filtered.map((o, idx) => ({
        ...o,
        __fit: computeFitScore(o, founderProfile) ?? 0,
        __idx: idx,
      }));
      withFit.sort((a, b) => (b.__fit - a.__fit) || ((b.weighted_total || 0) - (a.weighted_total || 0)) || (a.__idx - b.__idx));
      return withFit;
    }
    return filtered;
  })();

  const exportCSV = () => {
    const headers = ["Problem","Score","Verdict","Signal","User Type","Reddit","Freelancer","Price","Notes","Status","Found (created)","Scored","Updated"];
    const rows = filteredOpportunities.map(o => [
      `"${(o.problem || "").replace(/"/g, '""')}"`,
      Math.round(o.weighted_total || 0),
      o.verdict || "",
      o.signal_strength || "",
      `"${(o.user_type || "").replace(/"/g, '""')}"`,
      o.reddit_frequency || 0,
      o.freelancer_frequency || 0,
      o.suggested_price || 0,
      `"${(o.notes || "").replace(/"/g, '""')}"`,
      o.status || "",
      o.created_date || "",
      o.scored_date || "",
      o.updated_date || "",
    ]);
    const csv = [headers.join(","), ...rows.map(r => r.join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `painToProfit_${filter}_${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportBackup = async () => {
    try {
      const resp = await axios.get(`${API}/backup/export`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `paintoprofit_backup_${new Date().toISOString().replace(/[:.]/g, "-")}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert("Backup export failed: " + (e.response?.data?.detail || e.message));
    }
  };

  const restoreFileRef = useRef(null);
  const handleRestoreFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!window.confirm("Restore will MERGE this backup into the current database (existing records are preserved, missing ones are added). Continue?")) return;
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      if (!data.collections) throw new Error("Invalid backup file: missing 'collections'");
      const resp = await axios.post(`${API}/backup/restore`, { ...data, mode: "merge" });
      const counts = Object.entries(resp.data.restored || {})
        .map(([k, v]) => `${k}: ${v.upserted ?? v.inserted ?? 0}`)
        .join("\n");
      alert("Restore complete:\n\n" + counts);
      fetchData();
    } catch (err) {
      alert("Restore failed: " + (err.response?.data?.detail || err.message));
    }
  };

  // Admin console — takes absolute precedence (gated by its own password).
  const [adminRoute, setAdminRoute] = useState(() =>
    typeof window !== "undefined" && window.location.pathname.startsWith("/admin")
  );
  useEffect(() => {
    const onPop = () => setAdminRoute(window.location.pathname.startsWith("/admin"));
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);
  // Legal routes — derive once from the path (full-page nav; no SPA transition needed).
  const legalRoute = (() => {
    const p = typeof window !== "undefined" ? window.location.pathname : "";
    if (p.startsWith("/terms")) return "terms";
    if (p.startsWith("/privacy")) return "privacy";
    return null;
  })();

  if (adminRoute) return <AdminRoot />;

  // Public legal pages — /terms and /privacy (also linked from the cookie banner).
  if (legalRoute) return <LegalPage kind={legalRoute} />;

  // Public shareable opportunity view — takes precedence over all other routes.
  if (publicOppId !== null) {
    return (
      <OpportunityPublicView
        oppId={publicOppId}
        onEnterApp={() => {
          window.history.pushState({}, "", "/");
          setPublicOppId(null);
          setActiveTab(user ? "ai_pulse" : "landing");
        }}
      />
    );
  }

  // Public shareable brief view at /brief/{id}
  if (publicBriefId !== null) {
    return (
      <BriefPublicView
        briefId={publicBriefId}
        onEnterApp={() => {
          window.history.pushState({}, "", "/");
          setPublicBriefId(null);
          setActiveTab(user ? "ai_pulse" : "landing");
        }}
      />
    );
  }

  // Public landing page only on the explicit "landing" tab.
  // This is rendered FIRST (before any loading splash) so visitors hitting the
  // homepage never get blocked by slow/failing API calls — landing page is
  // pure marketing content and doesn't depend on /api/stats etc.
  if (authReady && activeTab === "landing") {
    return (
      <>
        <CountryCoverageBanner />
        <LandingPage
          onSignUp={() => setActiveTab("opportunities")}
          onSignIn={() => { setAuthMode("login"); setAuthOpen(true); }}
          onPricing={() => setActiveTab("pricing")}
          onDiscover={() => setActiveTab("discover")}
          onEvaluate={() => setActiveTab("evaluate")}
          onTrending={() => setActiveTab("trending")}
          onBestCountry={() => setActiveTab("country_fit")}
          onGuide={() => setActiveTab("guide")}
        />
        <AuthModal
          open={authOpen}
          onClose={() => setAuthOpen(false)}
          defaultMode={authMode}
        />
      </>
    );
  }

  // Tabs that fetch their own data don't need to wait on the global dashboard
  // boot fetch — so deep-links (e.g. a shared /evaluate/{id}) render instantly
  // instead of sitting behind the "Booting…" splash.
  const SELF_LOADING_TABS = ["evaluate", "discover", "trending", "ai_pulse", "briefs", "pricing", "account", "country_fit", "copilot", "guide", "reports", "jobs"];
  if (loading && !SELF_LOADING_TABS.includes(activeTab)) {
    return (
      <div className="min-h-screen bg-[#F1F3F8] flex items-center justify-center">
        <div className="text-center">
          <div className="w-10 h-10 mx-auto mb-4 border-2 border-[#8B5CF6] border-t-transparent rounded-full animate-spin" />
          <p className="font-mono-data text-xs uppercase tracking-[0.25em] text-[#5A575B]">Booting Prime Pillar…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F1F3F8] text-[#0E0E0E] lum-paint font-body">
      {/* Cinematic ambient glow */}
      <div className="fixed inset-0 pointer-events-none z-0 opacity-50">
        <div className="absolute top-0 right-[10%] w-[600px] h-[600px] rounded-full bg-[#8B5CF6]/15 blur-[140px]" />
        <div className="absolute bottom-0 left-[10%] w-[500px] h-[500px] rounded-full bg-[#5E73C1]/20 blur-[120px]" />
      </div>
      <div className="relative z-10">
      {/* Header */}
      <AppHeader
        user={user}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        setShowSettings={setShowSettings}
        openAuth={(mode) => { setAuthMode(mode); setAuthOpen(true); }}
      />

      {/* ===== Mobile bottom tab bar (lg:hidden) ===== */}
      <MobileBottomNav
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        onOpenMenu={() => setMobileMenuOpen(true)}
      />

      {/* ===== Mobile full-screen menu (lg:hidden) ===== */}
      <MobileMenuDrawer
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        user={user}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        openAuth={(mode) => { setAuthMode(mode); setAuthOpen(true); }}
      />

      {/* ===== Floating help button + first-visit tour callout ===== */}
      <HelpLauncher activeTab={activeTab} onOpenGuide={() => setActiveTab("guide")} />

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 pt-4 sm:pt-6 pb-32 lg:pb-8">
        {FRESH_DOMAIN[activeTab] && (
          <div className="flex justify-end mb-2">
            <FreshnessStamp domain={FRESH_DOMAIN[activeTab]} />
          </div>
        )}
        {activeTab === "pricing" && (
          <PricingPage onRequireAuth={() => { setAuthMode("signup"); setAuthOpen(true); }} onClose={() => setActiveTab("dashboard")} />
        )}
        {activeTab === "account" && user && (
          <AccountPage onClose={() => setActiveTab("dashboard")} />
        )}
        {activeTab === "ai_pulse" && (
          <AIPulseView
            onSelectOpp={(opp) => setSelectedOpp(opp)}
            userTier={user?.tier || "anon"}
          />
        )}
        {activeTab === "trending" && (
          <TrendingTopics
            onSelectOpp={(opp) => setSelectedOpp(opp)}
            onNavigate={(tab) => setActiveTab(tab)}
          />
        )}
        {activeTab === "guide" && (
          <GuideDemo onNavigate={(tab) => setActiveTab(tab)} target={guideTarget} onConsumeTarget={() => setGuideTarget(null)} />
        )}
        {activeTab === "reports" && (
          <ReportsLibrary onNavigate={(tab) => setActiveTab(tab)} />
        )}
        {activeTab === "jobs" && (
          <JobsBoard />
        )}
        {activeTab === "pain_points" && (
          <PainPoints onNavigate={setActiveTab} />
        )}
        {activeTab === "commerce" && (
          <CommercePage />
        )}
        {activeTab === "discover" && (
          <div>
            <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-4 flex justify-center sm:justify-end">
              <CorpusToggle value={discoverMode} onChange={setDiscoverMode} testid="discover" />
            </div>
            {discoverMode === "vc" ? <BriefBrowser /> : <BootstrappedCategories />}
          </div>
        )}
        {activeTab === "evaluate" && (
          <div>
            <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-4 flex justify-center sm:justify-end">
              <CorpusToggle value={evaluateMode} onChange={setEvaluateMode} testid="evaluate" />
            </div>
            {evaluateMode === "vc" ? <EvaluateBrowser /> : <BootstrappedBrowser />}
          </div>
        )}
        {activeTab === "country_fit" && (
          <CountryFitBrowser />
        )}
        {activeTab === "copilot" && (
          <div className="max-w-4xl mx-auto px-1 sm:px-4 pt-2">
            <div className="mb-4">
              <div className="text-xs font-bold tracking-widest text-violet-600 mb-1">ADVISOR</div>
              <h1 className="text-2xl sm:text-3xl font-extrabold text-zinc-900 flex items-center gap-2">Ask anything. Get a grounded answer. <GuideHelpButton feature="advisor" /></h1>
              <p className="text-zinc-500 text-sm mt-1">Powered by your real data — scored demand, live revenue, and competitor checks. It cites its evidence and tells you honestly when it doesn't know.</p>
            </div>
            <CopilotChat />
          </div>
        )}
        {activeTab === "briefs" && (
          <div className="max-w-5xl mx-auto p-4 sm:p-6">
            <MyBriefsView />
          </div>
        )}
        {activeTab === "account" && !user && authReady && (
          <div className="max-w-md mx-auto py-12 text-center">
            <Lock size={40} className="text-zinc-500 mx-auto mb-3" />
            <h2 className="text-xl font-black text-zinc-900 mb-2">Please sign in</h2>
            <p className="text-zinc-500 mb-4 text-sm">Create a free account to unlock your watchlist and personal preferences.</p>
            <button
              onClick={() => { setAuthMode("login"); setAuthOpen(true); }}
              className="px-4 py-2 bg-white hover:bg-black text-white font-semibold rounded-lg"
              data-testid="account-signin-prompt-btn"
            >
              Sign in
            </button>
          </div>
        )}

        {activeTab === "dashboard" && (
          <div className="space-y-6">
            {/* 📲 PWA install prompt — hidden when already installed or recently dismissed */}
            <InstallAppBanner />
            <CookieConsentBanner />

            {/* 🏆 Conversion Streak — hidden if user has no outreach yet */}
            <StreakBadge />

            {/* 🆕 Fresh today section — highlights opportunities scored in the last 24h */}
            <FreshTodaySection
              onSelectOpp={(o) => setSelectedOpp(o)}
              onJumpToAIPulse={() => setActiveTab("ai_pulse")}
            />

            {/* Stats Grid — 2 rows: top row = raw signals per source, bottom row = pipeline output */}
            <div className="space-y-3">
              <div>
                <div className="text-[10px] font-mono-data uppercase tracking-[0.2em] text-zinc-500 mb-2 px-1">Raw signals harvested</div>
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
                  <StatCard label="Reddit" value={stats?.total_reddit_posts || 0} icon={Database} />
                  <StatCard label="Fiverr" value={stats?.total_fiverr_gigs || 0} icon={Database} />
                  <StatCard label="Upwork" value={stats?.apify_upwork || 0} icon={Database} accent="text-green-600" />
                  <StatCard label="YouTube" value={stats?.apify_youtube || 0} icon={Database} accent="text-red-500" />
                  <StatCard label="ProductHunt" value={stats?.apify_producthunt || 0} icon={Database} accent="text-orange-500" />
                  <StatCard label="G2 Reviews" value={stats?.apify_g2 || 0} icon={Database} accent="text-amber-500" />
                  <StatCard label="Trends" value={stats?.apify_trends || 0} icon={Database} accent="text-cyan-500" />
                </div>
              </div>
              <div>
                <div className="text-[10px] font-mono-data uppercase tracking-[0.2em] text-zinc-500 mb-2 px-1">Pipeline output</div>
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-4 gap-3">
                  <StatCard label="Signals" value={stats?.total_opportunities || 0} icon={Target} accent="text-violet-600" />
                  <StatCard label="Scored" value={stats?.scored_opportunities || 0} icon={ChartBar} />
                  <StatCard label="Top 70+" value={stats?.top_opportunities || 0} icon={TrendUp} accent="text-emerald-400" />
                  <StatCard label="Consultant" value={stats?.consultant_replacements || 0} icon={Lightning} />
                </div>
              </div>
            </div>

            {/* Live Apify spend widget — admin-only (renders nothing for non-admin viewers) */}
            <ApifyUsageWidget />

            {/* Scanner Control */}
            <IdeaValidator
              onResult={() => fetchData()}
              onViewInSignals={() => {
                setFilter("validated_ideas");
                setActiveTab("opportunities");
              }}
            />
            <ScanControl scanStatus={scanStatus} onStartScan={handleStartScan} onSyncAnalyze={handleSyncAnalyze} schedulerInfo={settings} />

            {/* Last AI Run diagnostics */}
            <LastAIRunCard />

            {/* Sources breakdown - clickable to filter Opportunities */}
            <SourcesBar
              activeSource={sourceFilter}
              onSelectSource={(src) => {
                setSourceFilter(src);
                if (src) setActiveTab("opportunities");
              }}
            />

            {/* Top 5 Report */}
            <Top5Report opportunities={top5} />

            {/* ⚡ Quick Win Quadrant — 2x2 matrix surfacing must-build opps */}
            <QuickWinQuadrant onSelectOpp={(o) => setSelectedOpp(o)} />

            {/* G2 Pain Index — top products with most negative paying-customer reviews */}
            <G2PainIndex />

            {/* Google Trends (Passive) */}
            <GoogleTrendsWidget trends={googleTrends} />
          </div>
        )}

        {activeTab === "opportunities" && (
          <div className="space-y-6">
            {/* 🆕 Fresh today carousel — same as dashboard, so users browsing Signals see what's new first */}
            <FreshTodaySection
              onSelectOpp={(o) => setSelectedOpp(o)}
            />
            {/* Upgrade banner removed — full access enabled for everyone */}
            {false && (!user || user.tier === "free") && filter !== "timeline" && (
              <UpgradeBanner
                onUpgrade={goToPricing}
                message={user ? "You're on Free — only top 5 opportunities are visible." : "You're not signed in — only the top 5 opportunities are visible. Sign up free to claim them."}
              />
            )}

            {/* Source filter banner */}
            {sourceFilter && (
              <div
                className="flex items-center justify-between gap-2 bg-violet-600/5 border border-violet-200 px-3 py-2"
                data-testid="active-source-filter"
              >
                <span className="text-sm text-violet-600 font-mono-data">
                  <span className="uppercase tracking-[0.15em] text-[10px] mr-2">Source filter</span>
                  <span className="capitalize">{sourceFilter === "hackernews" ? "Hacker News" : sourceFilter === "manual" ? "My Ideas" : sourceFilter}</span>
                </span>
                <button
                  onClick={() => setSourceFilter(null)}
                  className="text-xs font-mono-data text-violet-600 hover:text-violet-600 uppercase tracking-[0.15em]"
                  data-testid="clear-source-filter-btn"
                >
                  Clear ✕
                </button>
              </div>
            )}

            {/* 🎯 Niche Filter pill row — 17 broad market niches from NICHE_CATALOG.
                 ★ button on each pill stars that niche into "My Niches" (saved to backend). */}
            {niches.length > 0 && (
              <div data-testid="niche-filter-row" className="space-y-2">
                <div className="filter-scroll flex items-center gap-2 bg-white border border-zinc-200 rounded-2xl p-2 overflow-x-auto">
                  <span className="text-xs font-medium text-zinc-500 px-3 flex-shrink-0 uppercase tracking-wider">Niche</span>
                  <button
                    onClick={() => { setNicheFilter(null); if (sourceFilter) setSourceFilter(null); }}
                    className={`px-3.5 py-1.5 text-xs font-medium rounded-full transition-all whitespace-nowrap flex-shrink-0 ${!nicheFilter ? 'bg-violet-600 text-white' : 'text-zinc-700 hover:text-zinc-900 hover:bg-zinc-50 border border-zinc-200'}`}
                    data-testid="niche-filter-all"
                  >
                    All Niches
                  </button>
                  <button
                    onClick={() => setValidatedOnly((v) => !v)}
                    title="Show only opportunities surfaced on ≥2 distinct platforms (Reddit + G2 + Upwork etc.) — high-confidence signals"
                    className={`px-3.5 py-1.5 text-xs font-medium rounded-full transition-all whitespace-nowrap flex-shrink-0 flex items-center gap-1 ${validatedOnly ? 'bg-emerald-600 text-white' : 'text-emerald-700 bg-emerald-50 border border-emerald-200 hover:bg-emerald-100'}`}
                    data-testid="validated-only-toggle"
                  >
                    <span>{validatedOnly ? '✓✓' : '✓'}</span> Validated only
                  </button>
                  <button
                    onClick={() => setTrendingUp((v) => !v)}
                    title="Show only opportunities where Google Trends interest is climbing right now — momentum filter"
                    className={`px-3.5 py-1.5 text-xs font-medium rounded-full transition-all whitespace-nowrap flex-shrink-0 flex items-center gap-1 ${trendingUp ? 'bg-rose-600 text-white' : 'text-rose-700 bg-rose-50 border border-rose-200 hover:bg-rose-100'}`}
                    data-testid="trending-up-toggle"
                  >
                    <span>🔥</span> Trending up
                  </button>
                  {/* My Fit — re-rank by Founder Profile match (hidden when profile not set or skipped) */}
                  {founderProfile && !founderProfile.skipped && founderProfile.stack && (
                    <button
                      onClick={() => setFitRanking((v) => !v)}
                      title="Re-rank opportunities by how well they match your stack, budget, and weekly hours"
                      className={`px-3.5 py-1.5 text-xs font-medium rounded-full transition-all whitespace-nowrap flex-shrink-0 flex items-center gap-1 ${fitRanking ? 'bg-violet-600 text-white' : 'text-violet-700 bg-violet-50 border border-violet-200 hover:bg-violet-100'}`}
                      data-testid="my-fit-toggle"
                    >
                      <span>🎯</span> My Fit
                    </button>
                  )}
                  {/* Set up Founder Profile CTA — visible when user skipped or never set */}
                  {user && (profileSkipped || !founderProfile) && (
                    <button
                      onClick={() => setProfileModalOpen(true)}
                      title="3 quick questions — we'll personalize the feed to your stack, budget, and time"
                      className="px-3.5 py-1.5 text-xs font-medium rounded-full transition-all whitespace-nowrap flex-shrink-0 flex items-center gap-1 text-violet-700 bg-white border border-violet-200 hover:bg-violet-50"
                      data-testid="open-founder-profile-btn"
                    >
                      <span>🎯</span> Set up My Fit
                    </button>
                  )}
                  {niches.map(n => {
                    const isStarred = myNiches.includes(n.key);
                    const isActive = nicheFilter === n.key;
                    return (
                      <div
                        key={n.key}
                        className={`group flex items-center rounded-full transition-all flex-shrink-0 ${isActive ? 'bg-violet-600' : isStarred ? 'bg-amber-50 border border-amber-300' : n.is_ai ? 'bg-violet-50 border border-violet-200' : 'bg-white border border-zinc-200 hover:bg-zinc-50'}`}
                        data-testid={`niche-pill-${n.key}`}
                      >
                        <button
                          onClick={(e) => { e.stopPropagation(); toggleMyNiche(n.key); }}
                          title={isStarred ? `Remove ${n.label} from My Niches` : `Add ${n.label} to My Niches`}
                          className={`pl-2.5 pr-1 py-1.5 text-xs flex-shrink-0 transition-colors ${isStarred ? 'text-amber-500' : isActive ? 'text-white/70 hover:text-white' : 'text-zinc-400 hover:text-amber-500'}`}
                          data-testid={`niche-star-${n.key}`}
                        >
                          {isStarred ? '★' : '☆'}
                        </button>
                        <button
                          onClick={() => { setNicheFilter(n.key); if (sourceFilter) setSourceFilter(null); }}
                          className={`pr-3.5 pl-1 py-1.5 text-xs font-medium whitespace-nowrap ${isActive ? 'text-white' : isStarred ? 'text-amber-800' : n.is_ai ? 'text-violet-700' : 'text-zinc-700'}`}
                          data-testid={`niche-filter-${n.key}`}
                        >
                          {n.label}
                        </button>
                      </div>
                    );
                  })}
                </div>

                {/* My Niches summary strip */}
                {myNiches.length > 0 && !nicheFilter && !sourceFilter && (
                  <div
                    className="flex items-center justify-between gap-2 bg-amber-50 border border-amber-200 px-3 py-2 rounded-md"
                    data-testid="my-niches-banner"
                  >
                    <span className="text-xs text-amber-800 font-mono-data flex items-center gap-2">
                      <span className="text-amber-500 text-sm">★</span>
                      <span className="uppercase tracking-[0.15em] text-[10px]">My Niches active</span>
                      <span className="text-amber-700/80">·</span>
                      <span className="font-semibold">{myNiches.length} {myNiches.length === 1 ? 'niche' : 'niches'} starred</span>
                      <span className="text-amber-700/80">·</span>
                      <span>{personalize ? 'Sorted to top' : 'Not personalized'}</span>
                    </span>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setPersonalize(p => !p)}
                        className="text-[10px] font-mono-data text-amber-800 hover:text-amber-900 uppercase tracking-[0.15em] underline-offset-2 hover:underline"
                        data-testid="toggle-personalize-btn"
                      >
                        {personalize ? 'Disable sort' : 'Enable sort'}
                      </button>
                      <button
                        onClick={() => { setMyNiches([]); axios.put(`${API}/me/niches`, { selected_niches: [] }).catch(() => {}); }}
                        className="text-[10px] font-mono-data text-amber-700 hover:text-amber-900 uppercase tracking-[0.15em] underline-offset-2 hover:underline"
                        data-testid="clear-my-niches-btn"
                      >
                        Clear all
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Active niche banner */}
            {nicheFilter && (
              <div
                className="flex items-center justify-between gap-2 bg-violet-600/5 border border-violet-200 px-3 py-2 rounded-md"
                data-testid="active-niche-filter"
              >
                <span className="text-sm text-violet-700 font-mono-data">
                  <span className="uppercase tracking-[0.15em] text-[10px] mr-2">Niche filter</span>
                  <span>{niches.find(n => n.key === nicheFilter)?.label || nicheFilter}</span>
                  <span className="ml-2 text-zinc-500">· {filteredOpportunities.length} {filteredOpportunities.length === 1 ? 'opportunity' : 'opportunities'}</span>
                </span>
                <button
                  onClick={() => setNicheFilter(null)}
                  className="text-xs font-mono-data text-violet-600 hover:text-violet-700 uppercase tracking-[0.15em]"
                  data-testid="clear-niche-filter-btn"
                >
                  Clear ✕
                </button>
              </div>
            )}

            {/* Filter Bar */}
            <div className="filter-scroll flex items-center gap-2 bg-white border border-zinc-200 rounded-2xl p-2">
              <span className="text-xs font-medium text-zinc-500 px-3 flex-shrink-0">View</span>
              {[
                { key: "all", label: "All" },
                { key: "new_today", label: "🆕 New today" },
                { key: "fresh_week", label: "🔥 This week" },
                { key: "validated_ideas", label: "Validated" },
                { key: "homerun", label: "Home Runs" },
                { key: "promising", label: "Promising" },
                { key: "consultant", label: "Consultant" },
                { key: "timeline", label: "Timeline" },
              ].map(f => (
                <button
                  key={f.key}
                  onClick={() => setFilter(f.key)}
                  className={`px-3.5 py-1.5 text-xs font-medium rounded-full transition-all whitespace-nowrap flex-shrink-0 ${filter === f.key ? 'bg-zinc-900 text-white' : 'text-zinc-700 hover:text-zinc-900 hover:bg-zinc-50'}`}
                  data-testid={`filter-${f.key}`}
                >
                  {f.label}
                </button>
              ))}
              {filter !== "timeline" && (
                <div className="ml-auto flex items-center gap-2 flex-shrink-0">
                  <span className="text-xs text-zinc-500 whitespace-nowrap hidden sm:inline px-1">{filteredOpportunities.length} signals</span>
                  {filteredOpportunities.length > 0 && (
                    <button
                      onClick={exportCSV}
                      className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-zinc-700 bg-zinc-50 border border-zinc-200 rounded-full hover:text-zinc-900 hover:border-zinc-300 transition-all whitespace-nowrap"
                      data-testid="export-csv-btn"
                    >
                      <Export size={12} /> CSV
                    </button>
                  )}
                  <button
                    onClick={exportBackup}
                    title="Download full JSON backup"
                    className="flex items-center gap-1 px-3 py-1.5 text-xs font-semibold text-violet-600 bg-violet-600/10 border border-violet-200 rounded-full hover:bg-violet-600/20 transition-all whitespace-nowrap"
                    data-testid="backup-export-btn"
                  >
                    <Database size={12} /> Backup
                  </button>
                  <button
                    onClick={() => restoreFileRef.current?.click()}
                    title="Restore from a JSON backup"
                    className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-zinc-700 bg-zinc-50 border border-zinc-200 rounded-full hover:text-zinc-900 hover:border-zinc-300 transition-all whitespace-nowrap"
                    data-testid="backup-restore-btn"
                  >
                    <ArrowClockwise size={12} /> Restore
                  </button>
                  <input
                    ref={restoreFileRef}
                    type="file"
                    accept="application/json,.json"
                    onChange={handleRestoreFile}
                    className="hidden"
                    data-testid="backup-restore-input"
                  />
                </div>
              )}
            </div>

            {/* Timeline View */}
            {filter === "timeline" && <TimelineView />}

            {/* Opportunities Grid */}
            {filter !== "timeline" && (
              <>
                {/* Thin-results hint — when Validated filter is on and results are scarce */}
                {validatedOnly && filteredOpportunities.length < 5 && filter !== "validated_ideas" && (
                  <div
                    className="rounded-2xl ring-1 ring-amber-200/80 bg-gradient-to-r from-amber-50 via-white to-white px-5 sm:px-6 py-4 sm:py-5 mb-4 shadow-[0_8px_24px_-20px_rgba(180,140,40,0.3)]"
                    data-testid="signals-thin-results-hint"
                  >
                    <div className="flex items-start gap-4 sm:gap-5 flex-wrap">
                      <div className="shrink-0 w-11 h-11 sm:w-12 sm:h-12 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center text-xl" aria-hidden>
                        ✓✓
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="font-display text-base sm:text-lg font-bold text-amber-800 leading-tight">
                          Only {filteredOpportunities.length} signal{filteredOpportunities.length === 1 ? "" : "s"} match Validated + non-AI
                        </div>
                        <p className="mt-1 text-[12.5px] sm:text-sm text-zinc-600 leading-snug">
                          Multi-source non-AI opportunities are rare — most validation happens once we hit ≥2 platforms. Toggle off <span className="font-semibold text-emerald-700">✓✓ Validated</span> for the full feed, or check AI Pulse for AI-tagged validated opportunities.
                        </p>
                        <div className="mt-3 flex items-center gap-2 flex-wrap">
                          <button
                            type="button"
                            onClick={() => setValidatedOnly(false)}
                            className="text-[12px] font-semibold px-3 py-1.5 rounded-full bg-zinc-900 text-white hover:bg-zinc-700 transition"
                            data-testid="signals-hint-clear-validated-btn"
                          >
                            Show all signals
                          </button>
                          <button
                            type="button"
                            onClick={() => setActiveTab("ai_pulse")}
                            className="text-[12px] font-semibold px-3 py-1.5 rounded-full bg-white ring-1 ring-zinc-300 text-zinc-700 hover:bg-zinc-100 transition"
                            data-testid="signals-hint-go-ai-pulse-btn"
                          >
                            Check AI Pulse →
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Trending-up hint — when filter is on but no rising opps yet (fresh data pending) */}
                {trendingUp && filteredOpportunities.length === 0 && filter !== "validated_ideas" && (
                  <div
                    className="rounded-2xl ring-1 ring-rose-200/80 bg-gradient-to-r from-rose-50 via-white to-white px-5 sm:px-6 py-4 sm:py-5 mb-4"
                    data-testid="signals-trending-empty-hint"
                  >
                    <div className="flex items-start gap-4 sm:gap-5 flex-wrap">
                      <div className="shrink-0 w-11 h-11 sm:w-12 sm:h-12 rounded-full bg-rose-100 text-rose-700 flex items-center justify-center text-xl" aria-hidden>
                        🔥
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="font-display text-base sm:text-lg font-bold text-rose-800 leading-tight">
                          No keywords trending up in the current cache
                        </div>
                        <p className="mt-1 text-[12.5px] sm:text-sm text-zinc-600 leading-snug">
                          Google Trends data refreshes on the next scheduled scan. You can also hit <span className="font-semibold">Refresh trends</span> on the Trending tab to pull fresh data (may rate-limit briefly).
                        </p>
                        <div className="mt-3 flex items-center gap-2 flex-wrap">
                          <button
                            type="button"
                            onClick={() => setTrendingUp(false)}
                            className="text-[12px] font-semibold px-3 py-1.5 rounded-full bg-zinc-900 text-white hover:bg-zinc-700 transition"
                            data-testid="signals-hint-clear-trending-btn"
                          >
                            Show all signals
                          </button>
                          <button
                            type="button"
                            onClick={() => setActiveTab("trending")}
                            className="text-[12px] font-semibold px-3 py-1.5 rounded-full bg-white ring-1 ring-zinc-300 text-zinc-700 hover:bg-zinc-100 transition"
                            data-testid="signals-hint-go-trending-btn"
                          >
                            Open Trending →
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {filteredOpportunities.length === 0 ? (
                  <Card className="text-center py-12">
                    <Target size={48} className="text-zinc-700 mx-auto mb-4" />
                    <h3 className="text-lg font-bold text-zinc-500 mb-2">
                      {filter === "validated_ideas" ? "No Validated Ideas Yet" : "No Opportunities Found"}
                    </h3>
                    <p className="text-zinc-500 mb-4">
                      {filter === "validated_ideas"
                        ? "Use the Idea Validator on the Dashboard to score your business ideas. They'll appear here."
                        : validatedOnly
                        ? "No multi-source non-AI signals matched. Toggle off ✓✓ Validated for the full feed, or hop over to AI Pulse."
                        : "Run a full scan to discover micro-SaaS opportunities from Reddit and Upwork."}
                    </p>
                    {filter !== "validated_ideas" && !validatedOnly && (
                      <Button variant="primary" onClick={() => handleStartScan("full")} data-testid="empty-state-scan-btn">
                        <Crosshair size={18} className="mr-2" /> Start Full Scan
                      </Button>
                    )}
                    {filter !== "validated_ideas" && validatedOnly && (
                      <div className="flex items-center justify-center gap-2 flex-wrap">
                        <Button variant="primary" onClick={() => setValidatedOnly(false)} data-testid="empty-state-clear-validated-btn">
                          Show all signals
                        </Button>
                        <Button variant="secondary" onClick={() => setActiveTab("ai_pulse")} data-testid="empty-state-go-ai-pulse-btn">
                          Check AI Pulse →
                        </Button>
                      </div>
                    )}
                    {filter === "validated_ideas" && (
                      <Button variant="primary" onClick={() => setActiveTab("dashboard")} data-testid="go-to-validator-btn">
                        <MagnifyingGlass size={18} className="mr-2" /> Go to Idea Validator
                      </Button>
                    )}
                  </Card>
                ) : (
                  <>
                    <ProArbitrageDiscoveryBanner onOpenPage={() => setActiveTab("country_fit")} />
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                      {filteredOpportunities.map(opp => (
                        <OpportunityCard 
                          key={opp.id} 
                          opportunity={opp} 
                          onSelect={setSelectedOpp}
                          onAction={handleAction}
                          onNotesUpdate={(id, val) => setOpportunities(prev => prev.map(o => o.id === id ? {...o, notes: val} : o))}
                          isStarred={starred.includes(opp.id)}
                          onToggleStar={toggleStar}
                          requireAuth={requireAuth}
                          onUpgrade={goToPricing}
                        />
                      ))}
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        )}
      </main>

      {/* Modals */}
      {selectedOpp && (
        <OpportunityDetail 
          opportunity={selectedOpp} 
          onClose={() => setSelectedOpp(null)}
          onAction={handleAction}
        />
      )}
      
      {showSettings && (
        <SettingsPanel 
          settings={settings} 
          onClose={() => setShowSettings(false)}
        />
      )}

      <AuthModal
        open={authOpen}
        onClose={() => setAuthOpen(false)}
        defaultMode={authMode}
      />

      <FounderProfileModal
        open={profileModalOpen}
        onClose={() => setProfileModalOpen(false)}
        onSaved={onProfileSaved}
      />
      </div>
    </div>
  );
}

export default App;
